import { OutboundAttemptGuard, acceptSdkCallEvent, OUTBOUND_PREPARATION_TIMEOUT_MS } from "@shared/outbound-attempt-guard";
// Pinned SDK version target: plivo-browser-sdk@2.2.21.
// NPM install was blocked by the environment security policy (tar@6.2.1 transitive
// dep is on the deny list). The SDK is therefore loaded from the Plivo CDN in
// client/index.html; the script tag has been version-annotated there.
//
// Verified API surface from plivo-browser-sdk@2.2.21 .d.ts (npm pack preview):
//   new Plivo(options): PlivoInstance
//   client.login(username, password): boolean  ← false = SDK rejected
//   client.logout(): void                      ← unregisters endpoint & clears socket
//   client.call(dest, extraHeaders?): any
//   client.answer(callUUID: string): void
//   client.hangup(): void
//   client.reject(): void
//   client.mute(): void
//   client.unmute(): void
//   client.sendDtmf(digit: string): void
//   client.getCallUUID(): string | undefined
//   client.on(event, handler): void

import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient, invalidatePrefix } from "@/lib/queryClient";
import { isAnonymousCallerToken, classifyCallerIdentity, type CallerKind } from "@shared/caller-identity";
import { useAuth } from "@/lib/auth-context";
import { useHeartbeat } from "@/hooks/use-heartbeat";
import { useToast } from "@/hooks/use-toast";
import { getTenantChannel, releaseTenantChannel, subscribeToReconnect } from "@/lib/pusher-client";
import type { SipReregisterPayload } from "@shared/pusher-events";
import type { SipRecoveryRejectionReason } from "@shared/types";
import { parseRecordingCaptureCommandResult } from "@shared/recording-ui";
import type { IncomingInternalCall } from "@/lib/extension-call-display";

declare global {
  interface Window {
    Plivo: any;
  }
}

// ─── SDK-instance handle ──────────────────────────────────────────────────────
// One handle per Plivo SDK instance.  markObsolete() disposes the handle and
// cancels all timers; isCurrent() lets every callback verify it is still live.
type SdkHandle = {
  id: number;
  sdk: any;
  handshakeTimer: ReturnType<typeof setTimeout> | null;
  audioWatchdog: ReturnType<typeof setInterval> | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
  loginInProgress: boolean;
  disposed: boolean;
  /**
   * Reason this handle was created. Controls whether onLogin applies statusAfterLogin.
   *   'initial'          — first-time connect on page load (apply statusAfterLogin)
   *   'manual'           — explicit user-initiated reconnect (apply statusAfterLogin)
   *   'automatic_retry'  — backoff/handshake-timeout retry (skip statusAfterLogin)
   *   'health_recovery'  — SIP health recovery reconnect (skip statusAfterLogin)
   */
  reason: 'initial' | 'manual' | 'automatic_retry' | 'health_recovery';
};

export type CallState = "idle" | "ringing" | "in-progress" | "incoming";

export type WarmTransferPhase = "idle" | "consultation" | "connected" | "merged" | "cancelling";

interface PlivoContextType {
  isRegistered: boolean;
  callState: CallState;
  callDirection: "inbound" | "outbound";
  currentCallUuid: string | null;
  callerName: string;
  dialedNumber: string;
  activeCustomerNumber: string;
  callerKind: CallerKind;
  ourNumber: string;
  incomingInternalCall: IncomingInternalCall | null;
  isCallbackActiveCall: boolean;
  callDuration: number;
  isMuted: boolean;
  isOnHold: boolean;
  isRecordingPaused: boolean;
  connectedEndpointId: string | null;
  activeContactId: string | null;
  activeCallLogId: string | null;
  activePdCampaignId: string | null;
  pendingDispositionCallLogId: string | null;
  pendingDispositionContactId: string | null;
  campaignDispositionConfig: { campaignId: string; campaignContactId: string; contactId?: string; forceDisposition: boolean; onDone: () => void } | null;
  setCampaignDispositionConfig: (config: { campaignId: string; campaignContactId: string; contactId?: string; forceDisposition: boolean; onDone: () => void } | null) => void;
  isDispositionBlocked: boolean;
  isTransferring: boolean;
  transferError: string | null;
  warmTransferPhase: WarmTransferPhase;
  warmTransferTarget: string | null;
  clearPendingDisposition: () => void;
  login: (username: string, password: string, endpointId?: string) => void;
  autoConnect: () => Promise<void>;
  disconnect: () => void;
  makeCall: (number: string, callerId?: string, campaignId?: string, displayCallerId?: string) => void;
  dncWarning: { type: string; contactName: string | null } | null;
  clearDncWarning: () => void;
  answerCall: (overrideUuid?: string) => void;
  hangupCall: () => void;
  rejectCall: () => void;
  toggleMute: () => void;
  toggleHold: () => Promise<void>;
  pauseRecording: () => Promise<void>;
  resumeRecording: () => Promise<void>;
  sendDtmf: (digit: string) => void;
  transferCall: (target: string, type: "cold" | "warm", displayLabel?: string) => Promise<void>;
  mergeTransfer: () => Promise<void>;
  completeTransfer: () => Promise<void>;
  cancelTransfer: () => Promise<void>;
  transferToSurvey: (surveyTemplateId?: string) => Promise<void>;
  registrationError: string | null;
  micBlockedDialogOpen: boolean;
  setMicBlockedDialogOpen: (open: boolean) => void;
  micBlockedMode: "inbound" | "outbound";
  plivoRef: React.MutableRefObject<any>;
  incomingQueueName: string;
  incomingIvrMenuName: string;
  incomingCampaignName: string;
  monitor: {
    active: boolean;
    sessionId: string | null;
    callLogId: string | null;
    mode: "listen" | "barge" | "whisper";
    targetName: string | null;
    agentName: string | null;
    contactNumber: string | null;
    callStartedAt: number | null;
    callUuid: string | null;
    connectedAt: number | null;
    setMode: (mode: "listen" | "barge" | "whisper") => void;
  };
  startMonitor: (opts: {
    sessionId: string;
    callLogId: string;
    mode: "listen" | "barge" | "whisper";
    targetName?: string | null;
    agentName?: string | null;
    contactNumber?: string | null;
    callStartedAt?: string | null;
  }) => void;
  hangupMonitor: () => void;
  expectMonitorCall: () => void;
  cancelMonitorExpect: () => void;
  lastCallEndReason: string | null;
  clearCallEndReason: () => void;
  /** Initiate SIP re-registration with a known health episode ID. */
  requestSipReconnect: (healthEpisodeId: string | null) => void;
}

function readApiMessage(payload: unknown, fallback: string): string {
  if (
    typeof payload === "object"
    && payload !== null
    && "message" in payload
    && typeof payload.message === "string"
  ) {
    return payload.message;
  }
  return fallback;
}

function readResolvedCallerId(payload: unknown): string | null {
  if (
    typeof payload === "object"
    && payload !== null
    && "callerId" in payload
    && typeof payload.callerId === "string"
  ) {
    return payload.callerId;
  }
  return null;
}

const PlivoContext = createContext<PlivoContextType | null>(null);

async function logCallToBackend(data: {
  direction: string;
  status: string;
  fromNumber: string;
  toNumber: string;
  duration?: number;
  callUuid?: string | null;
  contactName?: string | null;
}) {
  try {
    await apiRequest("POST", "/api/call-logs", {
      ...data,
      startedAt: new Date().toISOString(),
    });
    invalidatePrefix("/api/call-logs");
  } catch (e) {
    console.error("Failed to log call:", e);
  }
}

async function updateCallLog(id: string, data: Record<string, any>) {
  try {
    await apiRequest("PATCH", `/api/call-logs/${id}`, data);
    invalidatePrefix("/api/call-logs");
  } catch (e) {
    console.error("Failed to update call log:", e);
    setTimeout(async () => {
      try {
        await apiRequest("PATCH", `/api/call-logs/${id}`, data);
        invalidatePrefix("/api/call-logs");
        invalidatePrefix("/api/dashboard-stats");
      } catch (retryErr) {
        console.error("Retry failed for call log update:", retryErr);
      }
    }, 3000);
  }
}

// ─── Backoff schedule ─────────────────────────────────────────────────────────
const BACKOFF_DELAYS_MS = [0, 3_000, 6_000, 12_000, 30_000];

export function PlivoProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isRegistered, setIsRegistered] = useState(false);
  const [callState, setCallState] = useState<CallState>("idle");
  const callStateRef = useRef<CallState>("idle");
  const [currentCallUuid, setCurrentCallUuid] = useState<string | null>(null);
  const [callerName, setCallerName] = useState("");
  const [dialedNumber, setDialedNumber] = useState("");
  const [activeCustomerNumber, setActiveCustomerNumber] = useState("");
  const [callerKind, setCallerKind] = useState<CallerKind>('anonymous');
  const [ourNumber, setOurNumber] = useState("");
  const [incomingInternalCall, setIncomingInternalCall] = useState<IncomingInternalCall | null>(null);
  const [isCallbackActiveCall, setIsCallbackActiveCall] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isRecordingPaused, setIsRecordingPaused] = useState(false);
  const recordingCommandInProgressRef = useRef(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [micBlockedDialogOpen, setMicBlockedDialogOpen] = useState(false);
  const [micBlockedMode, setMicBlockedMode] = useState<"inbound" | "outbound">("inbound");
  const [connectedEndpointId, setConnectedEndpointId] = useState<string | null>(null);
  const connectedEndpointIdRef = useRef<string | null>(null);
  const [activeContactId, setActiveContactId] = useState<string | null>(null);
  const [activePdCampaignId, setActivePdCampaignId] = useState<string | null>(null);
  const [pendingDispositionCallLogId, setPendingDispositionCallLogId] = useState<string | null>(
    () => sessionStorage.getItem("pendingDispositionCallLogId"),
  );
  const [pendingDispositionContactId, setPendingDispositionContactId] = useState<string | null>(
    () => sessionStorage.getItem("pendingDispositionContactId"),
  );
  const [campaignDispositionConfig, setCampaignDispositionConfig] = useState<{ campaignId: string; campaignContactId: string; contactId?: string; forceDisposition: boolean; onDone: () => void } | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [dncWarning, setDncWarning] = useState<{ type: string; contactName: string | null } | null>(null);
  const clearDncWarning = useCallback(() => setDncWarning(null), []);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [warmTransferPhase, setWarmTransferPhase] = useState<WarmTransferPhase>("idle");
  const [warmTransferTarget, setWarmTransferTarget] = useState<string | null>(null);
  const [monitorActive, setMonitorActive] = useState(false);
  const [monitorSessionId, setMonitorSessionId] = useState<string | null>(null);
  const [monitorCallLogId, setMonitorCallLogId] = useState<string | null>(null);
  const [monitorMode, setMonitorMode] = useState<"listen" | "barge" | "whisper">("listen");
  const [monitorTargetName, setMonitorTargetName] = useState<string | null>(null);
  const [monitorAgentName, setMonitorAgentName] = useState<string | null>(null);
  const [monitorContactNumber, setMonitorContactNumber] = useState<string | null>(null);
  const [monitorCallStartedAt, setMonitorCallStartedAt] = useState<number | null>(null);
  const [monitorCallUuid, setMonitorCallUuid] = useState<string | null>(null);
  const [monitorConnectedAt, setMonitorConnectedAt] = useState<number | null>(null);
  const monitorPendingRef = useRef<{ sessionId: string; callLogId: string; mode: "listen" | "barge" | "whisper"; targetName: string | null; startedAt: number } | null>(null);
  const [lastCallEndReason, setLastCallEndReason] = useState<string | null>(null);
  const monitorCallUuidRef = useRef<string | null>(null);
  const monitorCallExpectedRef = useRef(false);
  const monitorCallExpectedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startMonitor = useCallback((opts: {
    sessionId: string;
    callLogId: string;
    mode: "listen" | "barge" | "whisper";
    targetName?: string | null;
    agentName?: string | null;
    contactNumber?: string | null;
    callStartedAt?: string | null;
  }) => {
    monitorPendingRef.current = { sessionId: opts.sessionId, callLogId: opts.callLogId, mode: opts.mode, targetName: opts.targetName ?? null, startedAt: Date.now() };
    setMonitorActive(true);
    setMonitorSessionId(opts.sessionId);
    setMonitorCallLogId(opts.callLogId);
    setMonitorMode(opts.mode);
    setMonitorTargetName(opts.targetName ?? null);
    setMonitorAgentName(opts.agentName ?? null);
    setMonitorContactNumber(opts.contactNumber ?? null);
    setMonitorCallStartedAt(opts.callStartedAt ? new Date(opts.callStartedAt).getTime() : null);
    setMonitorCallUuid(null);
    setMonitorConnectedAt(null);
  }, []);

  const hangupMonitor = useCallback(() => {
    const uuid = monitorCallUuidRef.current;
    if (uuid && plivoRef.current?.client) {
      try { plivoRef.current.client.hangup(); } catch {}
    }
    monitorPendingRef.current = null;
    monitorCallUuidRef.current = null;
    monitorCallExpectedRef.current = false;
    if (monitorCallExpectedTimerRef.current) {
      clearTimeout(monitorCallExpectedTimerRef.current);
      monitorCallExpectedTimerRef.current = null;
    }
    setMonitorActive(false);
    setMonitorSessionId(null);
    setMonitorCallLogId(null);
    setMonitorMode("listen");
    setMonitorTargetName(null);
    setMonitorAgentName(null);
    setMonitorContactNumber(null);
    setMonitorCallStartedAt(null);
    setMonitorCallUuid(null);
    setMonitorConnectedAt(null);
  }, []);

  const expectMonitorCall = useCallback(() => {
    monitorCallExpectedRef.current = true;
    if (monitorCallExpectedTimerRef.current) clearTimeout(monitorCallExpectedTimerRef.current);
    monitorCallExpectedTimerRef.current = setTimeout(() => {
      monitorCallExpectedRef.current = false;
      monitorCallExpectedTimerRef.current = null;
    }, 30_000);
  }, []);

  const cancelMonitorExpect = useCallback(() => {
    monitorCallExpectedRef.current = false;
    if (monitorCallExpectedTimerRef.current) {
      clearTimeout(monitorCallExpectedTimerRef.current);
      monitorCallExpectedTimerRef.current = null;
    }
  }, []);

  useEffect(() => { monitorCallUuidRef.current = monitorCallUuid; }, [monitorCallUuid]);

  const [incomingQueueName, setIncomingQueueName] = useState("");
  const [incomingIvrMenuName, setIncomingIvrMenuName] = useState("");
  const [incomingCampaignName, setIncomingCampaignName] = useState("");
  const activeTransferIdRef = useRef<string | null>(null);
  // Req 2: terminal-operation ownership ref — prevents onCallTerminated from racing with Complete.
  // Set to { transferId, action } before each terminal request; cleared only when the operation
  // reaches a confirmed terminal result or the transfer is fully finalized.
  const terminalTransferActionRef = useRef<{ transferId: string; action: "complete" | "cancel" } | null>(null);
  const { data: dispoSettings } = useQuery<{ dispositionEnabled: boolean; forceDisposition: boolean }>({
    queryKey: ["/api/disposition-settings"],
  });

  const isDispositionBlocked = useMemo(() => {
    return !!(pendingDispositionCallLogId && dispoSettings?.forceDisposition);
  }, [pendingDispositionCallLogId, dispoSettings?.forceDisposition]);

  useHeartbeat();

  // ─── Refs ───────────────────────────────────────────────────────────────────
  const plivoRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // acwTimerRef removed: ACW restoration is now fully server-authoritative.
  // The server timer uses an atomic guarded UPDATE so the browser must never
  // independently change agent status when ACW expires.
  const callStartRef = useRef<number | null>(null);
  const audioWatchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeCallLogIdRef = useRef<string | null>(null);
  const incomingCallUuidRef = useRef<string | null>(null);
  const outboundDialGuardRef = useRef(new OutboundAttemptGuard());
  useEffect(() => {
    const cancelPreparationOnLeave = () => {
      const guard = outboundDialGuardRef.current;
      const id = guard.id;
      if (!id || guard.started) return;
      guard.release(id);
      callStateRef.current = "idle";
      setCallState("idle");
      fetch("/api/outbound-call-context/cancel-pending", {
        method: "POST", credentials: "include", keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ outboundAttemptId: id, notes: "browser_left_during_preparation" }),
      }).catch(() => {});
    };
    window.addEventListener("pagehide", cancelPreparationOnLeave);
    return () => window.removeEventListener("pagehide", cancelPreparationOnLeave);
  }, []);
  const activeCallDirectionRef = useRef<"inbound" | "outbound">("outbound");
  const [callDirection, setCallDirection] = useState<"inbound" | "outbound">("outbound");
  const activeCallNumberRef = useRef<string>("");
  const activeContactIdRef = useRef<string | null>(null);
  const isPowerDialerCallRef = useRef<boolean>(false);
  const pdCampaignCallIdRef = useRef<string>("");
  const pdCampaignIdRef = useRef<string>("");
  const autoAnswerEnabledRef = useRef<boolean>(false);

  // ─── Warm-transfer audio/hold state refs ─────────────────────────────────────
  /** True when hold music was started by toggleHold (distinguishes intentional from forced mic-mute) */
  const remoteAudioIntentionallyMutedRef = useRef<boolean>(false);
  /** The isMuted state snapshotted BEFORE hold — used to restore mic correctly after unhold */
  const wasMutedBeforeHoldRef = useRef<boolean>(false);
  /** Lock to prevent concurrent hold/unhold races */
  const holdInProgressRef = useRef<boolean>(false);
  /** isMuted snapshot taken before transfer starts — used to restore mic after cancel/merge */
  const preTransferManualMuteRef = useRef<boolean>(false);
  /** Bounded poll interval for cancel confirmation (cleared in resetCallState) */
  const cancelPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── SIP resilience refs ─────────────────────────────────────────────────────
  const currentSdkHandleRef = useRef<SdkHandle | null>(null);
  const resetSdkRef = useRef<Promise<SdkHandle | null> | null>(null);
  const failureCountRef = useRef(0);
  const healthEpisodeIdRef = useRef<string | null>(null);
  const lastSipHealthCheckRef = useRef(0);
  const lastSipRecoverRef = useRef(0);
  const sipRecoverRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Deferred reconnect request stored when resetSdk() is called while a call is active.
   * Flushed synchronously from every terminal call path after callStateRef becomes 'idle'.
   */
  const pendingReconnectRef = useRef<{ reason: SdkHandle['reason']; healthEpisodeId?: string | null } | null>(null);

  const { data: userSettingsForAutoAnswer } = useQuery<{ autoAnswerEnabled?: boolean }>({
    queryKey: ["/api/user-settings"],
  });

  useEffect(() => {
    autoAnswerEnabledRef.current = userSettingsForAutoAnswer?.autoAnswerEnabled ?? false;
  }, [userSettingsForAutoAnswer?.autoAnswerEnabled]);

  // ─── Component-lifetime: cross-tab BroadcastChannel ──────────────────────────
  const crossTabChannelRef = useRef<BroadcastChannel | null>(null);
  const isLocalUpdateRef = useRef(false);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    try {
      const channel = new BroadcastChannel("callkarma_call_sync");
      crossTabChannelRef.current = channel;

      channel.onmessage = (event) => {
        const msg = event.data;
        if (!msg || !msg.type) return;
        if (user && msg.userId && msg.userId !== user.id) return;

        if (msg.type === "call_ended") {
          setCallState("idle");
          setCurrentCallUuid(null);
          setCallerName("");
          setDialedNumber("");
          setCallDuration(0);
          setIsMuted(false);
          setIsOnHold(false);
          setIsRecordingPaused(false);
          setWarmTransferPhase("idle");
          setWarmTransferTarget(null);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          invalidatePrefix("/api/call-logs");
          invalidatePrefix("/api/agent-statuses");
        } else if (msg.type === "call_started") {
          invalidatePrefix("/api/agent-statuses");
        } else if (msg.type === "status_changed") {
          invalidatePrefix("/api/agent-statuses");
        }
      };

      return () => {
        channel.close();
        crossTabChannelRef.current = null;
      };
    } catch (_e) {}
  }, [user]);

  const broadcastCallEvent = useCallback((type: string, data?: Record<string, any>) => {
    try {
      crossTabChannelRef.current?.postMessage({ type, userId: user?.id, ...data });
    } catch (_e) {}
  }, [user]);

  useEffect(() => { connectedEndpointIdRef.current = connectedEndpointId; }, [connectedEndpointId]);
  useEffect(() => { callStateRef.current = callState; }, [callState]);
  useEffect(() => { activeContactIdRef.current = activeContactId; }, [activeContactId]);

  // ─── Remote audio recovery (component-scoped, uses plivoRef) ─────────────────
  const recoverRemoteAudio = useCallback(() => {
    // Never unmute or restart remote audio while the agent has intentionally
    // silenced it (hold is active). The watchdog fires on a 3-second interval
    // and would otherwise undo the hold mute before the customer is off hold.
    if (remoteAudioIntentionallyMutedRef.current) return;
    try {
      const plivoAudio = plivoRef.current?.client?.audio;
      if (plivoAudio?.audioContext?.state === "suspended") {
        plivoAudio.audioContext.resume().catch(() => {});
        console.log("[Plivo] AudioContext was suspended — resumed");
      }
      const remoteAudio: HTMLVideoElement | HTMLAudioElement | null =
        (document.getElementById("plivo_webrtc_remoteview") as HTMLVideoElement | null) ??
        (plivoAudio?.remoteAudio as HTMLAudioElement | undefined) ??
        null;
      if (remoteAudio) {
        if (remoteAudio.muted) {
          remoteAudio.muted = false;
          console.log("[Plivo] Remote audio was muted — unmuted");
        }
        if (!remoteAudio.srcObject && plivoAudio?.remoteStream) {
          remoteAudio.srcObject = plivoAudio.remoteStream;
          console.log("[Plivo] Remote audio srcObject was null — reattached stream");
        }
        if (remoteAudio.paused && remoteAudio.srcObject) {
          remoteAudio.play().catch((e) => {
            console.warn("[Plivo] Remote audio play() failed:", e);
          });
          console.log("[Plivo] Remote audio was paused — triggered play()");
        }
      } else {
        document.querySelectorAll<HTMLAudioElement>("audio, video").forEach((el) => {
          if (el.srcObject && el.paused) {
            el.play().catch(() => {});
            console.log("[Plivo] DOM audio/video element was paused — triggered play()");
          }
        });
      }
    } catch (e) {}
  }, []);

  // ─── Component-lifetime: media device change listener ────────────────────────
  useEffect(() => {
    const handleDeviceChange = () => {
      if (audioWatchdogRef.current !== null) {
        console.log("[Plivo] mediaDevices devicechange detected during active call — running audio recovery");
        recoverRemoteAudio();
      }
    };
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    }
    return () => {
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
      }
    };
  }, [recoverRemoteAudio]);

  // ─── SIP resilience helpers ───────────────────────────────────────────────────

  function isCurrent(handle: SdkHandle): boolean {
    return !handle.disposed && currentSdkHandleRef.current?.id === handle.id;
  }

  function markObsolete(handle: SdkHandle): boolean {
    if (handle.disposed) return false;
    handle.disposed = true;
    clearTimeout(handle.handshakeTimer!);
    clearTimeout(handle.retryTimer!);
    clearInterval(handle.audioWatchdog!);
    handle.handshakeTimer = null;
    handle.retryTimer = null;
    handle.audioWatchdog = null;
    // Sync the component-level audioWatchdog ref when the current handle is disposed
    if (currentSdkHandleRef.current?.id === handle.id) {
      audioWatchdogRef.current = null;
    }
    return true;
  }

  function nextBackoffDelay(): number {
    const idx = Math.min(failureCountRef.current, BACKOFF_DELAYS_MS.length - 1);
    const base = BACKOFF_DELAYS_MS[idx];
    const jitter = base * 0.2 * (Math.random() * 2 - 1);
    return Math.max(0, Math.round(base + jitter));
  }

  function incrementFailureCounter(): void {
    // Do NOT clamp the counter here — it must be able to reach 5 (the escalation
    // threshold). The BACKOFF_DELAYS_MS index is clamped separately in nextBackoffDelay().
    failureCountRef.current += 1;
  }

  function maybeEscalate(): void {
    if (failureCountRef.current >= 5) {
      escalateToSipUnavailable().catch(() => {});
    }
  }

  function resetFailureCounter(): void {
    failureCountRef.current = 0;
  }

  function scheduleRetry(handle: SdkHandle, delayMs: number): void {
    clearTimeout(handle.retryTimer!);
    handle.retryTimer = setTimeout(() => {
      handle.retryTimer = null;
      if (!isCurrent(handle)) return;
      connectHandle(handle);
    }, delayMs);
  }

  function createSdkInstance(reason: SdkHandle['reason'] = 'initial'): SdkHandle {
    const options = {
      debug: "ALL",
      permOnClick: true,
      enableQualityTracking: "ALL",
      codecs: ["OPUS", "PCMU"],
      dscp: true,
      enableTracking: false,
      audioConstraints: {
        optional: [
          { googAutoGainControl: true },
          { googAutoGainControl2: true },
          { googEchoCancellation: true },
          { googNoiseSuppression: true },
          { googHighpassFilter: true },
          { googTypingNoiseDetection: true },
        ],
      },
      allowMultipleIncomingCalls: false,
    };
    const sdk = new window.Plivo(options);
    const handle: SdkHandle = {
      id: Date.now() + Math.random(),
      sdk,
      handshakeTimer: null,
      audioWatchdog: null,
      retryTimer: null,
      loginInProgress: false,
      disposed: false,
      reason,
    };
    plivoRef.current = sdk;
    currentSdkHandleRef.current = handle;
    return handle;
  }

  function disposeSdkInstance(handle: SdkHandle): void {
    if (markObsolete(handle)) {
      try { handle.sdk.client.logout(); }
      catch (err) { console.warn("[Plivo] SDK logout failed during dispose:", err); }
    }
  }

  async function connectHandle(handle: SdkHandle): Promise<void> {
    if (!isCurrent(handle)) return;
    if (handle.loginInProgress || handle.disposed) return;
    handle.loginInProgress = true;

    let username: string, password: string, endpointId: string;
    try {
      const res = await fetch("/api/sip/credentials", { credentials: "include" });
      if (!res.ok) {
        handle.loginInProgress = false;
        if (!isCurrent(handle)) return;
        incrementFailureCounter();
        maybeEscalate();
        scheduleRetry(handle, nextBackoffDelay());
        return;
      }
      ({ username, password, endpointId } = await res.json());
      if (!username || !password) {
        handle.loginInProgress = false;
        console.log("[Plivo] No SIP credentials available — not connecting");
        return;
      }
    } catch (err) {
      handle.loginInProgress = false;
      if (!isCurrent(handle)) return;
      incrementFailureCounter();
      maybeEscalate();
      scheduleRetry(handle, nextBackoffDelay());
      return;
    }

    if (!isCurrent(handle)) { handle.loginInProgress = false; return; }

    if (endpointId) {
      setConnectedEndpointId(endpointId);
      connectedEndpointIdRef.current = endpointId;
    }

    console.log("[Plivo] Attempting login with username:", username, "password length:", password.length);
    const accepted = handle.sdk.client.login(username, password);
    if (accepted === false) {
      handle.loginInProgress = false;
      incrementFailureCounter();
      maybeEscalate();
      scheduleRetry(handle, nextBackoffDelay());
      return;
    }

    // Start handshake timer — cleared by onLogin / onLoginFailed.
    handle.handshakeTimer = setTimeout(() => {
      handle.handshakeTimer = null;
      if (!isCurrent(handle)) return;
      console.warn("[Plivo] Handshake timeout — replacing SDK instance");
      incrementFailureCounter();
      maybeEscalate();
      replaceAndConnect('automatic_retry');
    }, 20_000);
    // loginInProgress is cleared by onLogin / onLoginFailed as their first synchronous action.
  }

  async function resetSdk(reason: SdkHandle['reason'] = 'automatic_retry'): Promise<SdkHandle | null> {
    if (callStateRef.current !== "idle") {
      // Call is active — defer the reconnect. It will be flushed by flushPendingReconnect()
      // at the end of the call's terminal path (onCallTerminated / onCallFailed / etc.)
      pendingReconnectRef.current = { reason, healthEpisodeId: healthEpisodeIdRef.current };
      console.log(`[Plivo] resetSdk deferred — call state is ${callStateRef.current}, stored pending reconnect (reason=${reason})`);
      return null;
    }
    if (resetSdkRef.current) return resetSdkRef.current;
    const p = (async () => {
      const old = currentSdkHandleRef.current;
      if (old) disposeSdkInstance(old);
      const handle = createSdkInstance(reason);
      bindSdkInstanceEvents(handle);
      return handle;
    })().finally(() => { resetSdkRef.current = null; });
    resetSdkRef.current = p;
    return p;
  }

  async function replaceAndConnect(reason: SdkHandle['reason'] = 'automatic_retry'): Promise<void> {
    const handle = await resetSdk(reason);
    if (!handle) return;
    await connectHandle(handle);
  }

  /**
   * Flush a deferred reconnect request stored while a call was active.
   * MUST be called synchronously from every terminal call path after
   * callStateRef.current has been set to 'idle'.
   */
  function flushPendingReconnect(): void {
    const pending = pendingReconnectRef.current;
    if (!pending) return;
    pendingReconnectRef.current = null;
    if (pending.healthEpisodeId) healthEpisodeIdRef.current = pending.healthEpisodeId;
    console.log(`[Plivo] flushPendingReconnect — initiating deferred reconnect (reason=${pending.reason})`);
    replaceAndConnect(pending.reason).catch(() => {});
  }

  // ─── SIP health utilities (Step 10–12) ────────────────────────────────────────

  async function checkSipHealthMe(): Promise<void> {
    const epId = connectedEndpointIdRef.current;
    if (!epId) return;
    const now = Date.now();
    if (now - lastSipHealthCheckRef.current < 10_000) return;
    lastSipHealthCheckRef.current = now;
    try {
      const r = await fetch("/api/sip/health/me", { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        if (data.healthEpisodeId && !healthEpisodeIdRef.current) {
          healthEpisodeIdRef.current = data.healthEpisodeId;
        }
      }
    } catch {}
  }

  async function escalateToSipUnavailable(): Promise<void> {
    const epId = connectedEndpointIdRef.current;
    if (!epId) return;
    const now = Date.now();
    if (now - lastSipHealthCheckRef.current < 10_000) return;
    lastSipHealthCheckRef.current = now;
    try {
      const r = await fetch("/api/sip/health/check-registration", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      // 409 still_unregistered is not cached — make a fresh request
      if (!r.ok) return;
      const data = await r.json();
      if (!data.opened) return; // check passed — endpoint is healthy
      const episodeId = data.healthEpisodeId || data.episodeId;
      if (episodeId) healthEpisodeIdRef.current = episodeId;
      await apiRequest("PUT", `/api/agent-statuses/${epId}/sip-unavailable`, {
        healthEpisodeId: episodeId,
      });
      console.log("[Plivo] Escalated to sip-unavailable — episode:", episodeId);
    } catch (e) {
      console.error("[Plivo] Escalation check failed:", e);
    }
  }

  async function attemptSipRecovery(episodeId: string): Promise<void> {
    const epId = connectedEndpointIdRef.current;
    if (!epId) return;
    const now = Date.now();
    if (now - lastSipRecoverRef.current < 10_000) return;
    lastSipRecoverRef.current = now;
    try {
      const r = await fetch("/api/sip/health/recover", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endpointId: epId, episodeId }),
      });
      if (r.ok) {
        healthEpisodeIdRef.current = null;
        console.log("[Plivo] SIP health episode cleared via recovery");
        return;
      }
      if (r.status === 409) {
        const data: { reason?: SipRecoveryRejectionReason } = await r.json().catch(() => ({}));
        if (data.reason === "still_unregistered") {
          // Retain ref; never cache this 409 — retry after ≥16s for a fresh Plivo check
          if (sipRecoverRetryTimerRef.current) clearTimeout(sipRecoverRetryTimerRef.current);
          sipRecoverRetryTimerRef.current = setTimeout(() => {
            sipRecoverRetryTimerRef.current = null;
            if (healthEpisodeIdRef.current === episodeId) {
              attemptSipRecovery(episodeId);
            }
          }, 16_000);
        } else {
          // episode_changed — clear ref and stop retrying
          healthEpisodeIdRef.current = null;
        }
      }
      // Network error: retain ref, retry on next onLogin
    } catch {
      // Network error: retain ref, retry on next onLogin
    }
  }

  // ─── SDK instance event binding ───────────────────────────────────────────────
  function bindSdkInstanceEvents(handle: SdkHandle): void {

    handle.sdk.client.on("onLogin", async () => {
      // Clear timers synchronously as first action
      clearTimeout(handle.handshakeTimer!);
      clearTimeout(handle.retryTimer!);
      handle.handshakeTimer = null;
      handle.retryTimer = null;
      handle.loginInProgress = false;

      if (!isCurrent(handle)) return;

      console.log("[Plivo] Login successful!");
      resetFailureCounter();
      setIsRegistered(true);
      setRegistrationError(null);

      // Step 12: Recovery acknowledgement — if there is an open health episode, try to close it
      const episodeId = healthEpisodeIdRef.current;
      if (episodeId) {
        attemptSipRecovery(episodeId);
      }

      // Step 11: Check SIP health on every successful login
      checkSipHealthMe().catch(() => {});

      const epId = connectedEndpointIdRef.current;
      if (!epId) return;

      // Retry delays: attempts 1-4 use 1500ms, final attempt uses 3000ms
      const RETRY_DELAYS = [1500, 1500, 1500, 3000];
      const MAX_RETRIES = RETRY_DELAYS.length + 1;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        if (!isCurrent(handle)) return;
        try {
          const currentStatusRes = await fetch(`/api/agent-statuses/${epId}`, { credentials: "include" });
          if (!isCurrent(handle)) return;
          if (currentStatusRes.status === 401) {
            if (attempt < MAX_RETRIES) {
              const delay = RETRY_DELAYS[attempt - 1];
              console.log(`[Plivo] Session not ready yet (attempt ${attempt}/${MAX_RETRIES}), retrying in ${delay}ms...`);
              await new Promise((r) => setTimeout(r, delay));
              continue;
            }
            console.warn("[Plivo] Session still not ready after retries — flushing stale query cache");
            invalidatePrefix("/api/agent-statuses");
            invalidatePrefix("/api/dashboard-stats");
            return;
          }

          if (!isCurrent(handle)) return;

          if (currentStatusRes.ok) {
            const currentStatusData = await currentStatusRes.json();
            if (!isCurrent(handle)) return;
            if (currentStatusData.status && currentStatusData.status !== "offline") {
              if (currentStatusData.status === "ringing") {
                console.log("[Plivo] Agent reconnected with stale 'ringing' status — clearing via call-rejected");
                try {
                  await apiRequest("POST", `/api/agent-statuses/${epId}/call-rejected`, {});
                } catch (e) {
                  console.error("[Plivo] Failed to clear stale ringing status:", e);
                }
                invalidatePrefix("/api/agent-statuses");
                invalidatePrefix("/api/dashboard-stats");
                return;
              }
              console.log("[Plivo] Agent already has active status:", currentStatusData.status, "— skipping login status override");
              invalidatePrefix("/api/agent-statuses");
              invalidatePrefix("/api/dashboard-stats");
              return;
            }
          }

          if (!isCurrent(handle)) return;

          try {
            const extStatusRes = await fetch("/api/my-extension-status", { credentials: "include" });
            if (extStatusRes.ok) {
              const extStatusData = await extStatusRes.json();
              if (extStatusData.extensionOnline) {
                console.log("[Plivo] Extension is already online — keeping platform endpoint offline");
                invalidatePrefix("/api/agent-statuses");
                invalidatePrefix("/api/dashboard-stats");
                return;
              }
            }
          } catch (_e) {}

          if (!isCurrent(handle)) return;

          // Only apply statusAfterLogin for initial/manual logins.
          // Automatic reconnects (backoff retries, health recovery) must not
          // override an agent's current status — they may be on a call or in ACW.
          if (handle.reason !== 'initial' && handle.reason !== 'manual') {
            console.log(`[Plivo] Skipping statusAfterLogin for reason='${handle.reason}' — preserving current agent status`);
            invalidatePrefix("/api/agent-statuses");
            invalidatePrefix("/api/dashboard-stats");
            return;
          }
          const settingsRes = await fetch("/api/tenant-settings", { credentials: "include" });
          if (!isCurrent(handle)) return;
          if (settingsRes.ok) {
            const settingsData = await settingsRes.json();
            const loginStatus = settingsData?.tenantBusinessSettings?.statusAfterLogin || "ready";
            const SYSTEM_STATUSES = ["ready", "not_ready", "on_call", "after_call_work", "offline"];
            const isSystem = SYSTEM_STATUSES.includes(loginStatus);
            await apiRequest("PUT", `/api/agent-statuses/${epId}`, {
              status: isSystem ? loginStatus : "not_ready",
              customLabel: isSystem ? null : loginStatus,
            });
            invalidatePrefix("/api/agent-statuses");
            invalidatePrefix("/api/dashboard-stats");
            console.log("[Plivo] Set agent status after login:", loginStatus);
          }
          return;
        } catch (err) {
          console.error("[Plivo] Failed to set status after login:", err);
          invalidatePrefix("/api/agent-statuses");
          invalidatePrefix("/api/dashboard-stats");
          return;
        }
      }
    });

    handle.sdk.client.on("onLoginFailed", (cause: string) => {
      // Clear timers synchronously as first action
      clearTimeout(handle.handshakeTimer!);
      clearTimeout(handle.retryTimer!);
      handle.handshakeTimer = null;
      handle.retryTimer = null;
      handle.loginInProgress = false;

      if (!isCurrent(handle)) return;

      console.error("[Plivo] Login failed with cause:", cause);
      incrementFailureCounter();
      setIsRegistered(false);
      setRegistrationError(cause || "Authentication Error");

      maybeEscalate();
      scheduleRetry(handle, nextBackoffDelay());
    });

    handle.sdk.client.on("onIncomingCall", async (caller: string, extraHeaders: any, callInfo: any) => {
      if (!isCurrent(handle)) return;
      const cleanCaller = (caller || "").replace(/@.*$/, "").replace(/^sip:/i, "");
      const displayCaller = cleanCaller || "Unknown";

      const getHeader = (hdrs: any, key: string): string | undefined => {
        if (!hdrs) return undefined;
        const lower = key.toLowerCase();
        for (const k of Object.keys(hdrs)) {
          if (k.toLowerCase() === lower) return hdrs[k];
        }
        return undefined;
      };

      console.log("[Plivo] onIncomingCall headers:", JSON.stringify(extraHeaders));

      const headerFlag = getHeader(extraHeaders, "X-PH-IsMonitorCall");
      const pendingMonitor = monitorPendingRef.current;
      const pendingWithinWindow = pendingMonitor && Date.now() - pendingMonitor.startedAt < 30_000;
      const isMonitorCall = headerFlag === "true" || monitorCallExpectedRef.current || !!pendingWithinWindow;

      if (isMonitorCall) {
        const sessionId = getHeader(extraHeaders, "X-PH-MonitorSessionId") || pendingMonitor?.sessionId || "";
        const monCallLogId = getHeader(extraHeaders, "X-PH-MonitorCallLogId") || pendingMonitor?.callLogId || "";
        const monMode = ((getHeader(extraHeaders, "X-PH-MonitorMode") || pendingMonitor?.mode || "listen")) as "listen" | "barge" | "whisper";
        console.log(`[Plivo] Incoming MONITOR call session=${sessionId} mode=${monMode} (header=${headerFlag ?? "missing"}, fallback=${!!pendingWithinWindow})`);
        if (callInfo?.callUUID && plivoRef.current?.client) {
          try { plivoRef.current.client.answer(callInfo.callUUID); } catch (e) { console.warn("[Monitor] answer failed", e); }
        }
        if (!pendingMonitor && sessionId && monCallLogId) {
          monitorPendingRef.current = { sessionId, callLogId: monCallLogId, mode: monMode, targetName: null, startedAt: Date.now() };
          setMonitorActive(true);
          setMonitorSessionId(sessionId);
          setMonitorCallLogId(monCallLogId);
          setMonitorMode(monMode);
        } else if (pendingMonitor) {
          pendingMonitor.startedAt = 0;
        }
        monitorCallExpectedRef.current = false;
        if (monitorCallExpectedTimerRef.current) {
          clearTimeout(monitorCallExpectedTimerRef.current);
          monitorCallExpectedTimerRef.current = null;
        }
        setMonitorCallUuid(callInfo?.callUUID || null);
        setMonitorConnectedAt(Date.now());
        return;
      }

      if (outboundDialGuardRef.current.id) {
        if (callInfo?.callUUID) handle.sdk.client.reject(callInfo.callUUID);
        return;
      }
      incomingCallUuidRef.current = callInfo?.callUUID || null;
      const ringEpId = connectedEndpointIdRef.current;
      console.log("[Plivo] onIncomingCall — connectedEndpointIdRef:", ringEpId);
      if (ringEpId) {
        apiRequest("POST", `/api/agent-statuses/${ringEpId}/ringing`, { callUuid: callInfo?.callUUID || null })
          .then(() => {
            invalidatePrefix("/api/agent-statuses");
            invalidatePrefix("/api/dashboard-stats");
          })
          .catch((err) => console.error("[Plivo] Failed to set ringing status:", err));
      } else {
        console.warn("[Plivo] connectedEndpointIdRef is null on incoming call — fetching from /api/sip/credentials");
        fetch("/api/sip/credentials", { credentials: "include" })
          .then(r => {
            if (!r.ok) throw new Error(`/api/sip/credentials returned ${r.status}`);
            return r.json() as Promise<{ endpointId: string; username: string; password: string }>;
          })
          .then((creds) => {
            const epId = creds.endpointId;
            if (!epId) throw new Error("No endpointId in sip/credentials response");
            connectedEndpointIdRef.current = epId;
            setConnectedEndpointId(epId);
            return apiRequest("POST", `/api/agent-statuses/${epId}/ringing`, { callUuid: callInfo?.callUUID || null });
          })
          .then(() => {
            invalidatePrefix("/api/agent-statuses");
            invalidatePrefix("/api/dashboard-stats");
          })
          .catch((err) => console.error("[Plivo] Fallback ringing via sip/credentials failed:", err));
      }

      const isInternalCall = extraHeaders?.["X-PH-IsInternal"] === "true" || extraHeaders?.["X-Ph-Isinternal"] === "true";
      const isCallbackCall = extraHeaders?.["X-PH-CallerName"] === "Callback" || extraHeaders?.["X-Ph-Callername"] === "Callback";
      const isAutomationCall = extraHeaders?.["X-PH-Source"] === "automation" || extraHeaders?.["X-Ph-Source"] === "automation";
      const automationContactId = extraHeaders?.["X-PH-ContactId"] || extraHeaders?.["X-Ph-Contactid"] || "";
      const isPowerDialerBridge = extraHeaders?.["X-PH-Source"] === "power-dialer" || extraHeaders?.["X-Ph-Source"] === "power-dialer";
      let internalCallerName = "";
      if (isInternalCall) {
        const rawName = extraHeaders?.["X-PH-CallerName"] || extraHeaders?.["X-Ph-Callername"] || "";
        internalCallerName = decodeURIComponent(rawName.replace(/\+/g, " "));
      }
      setIncomingInternalCall(isInternalCall ? {
        callerName: internalCallerName,
        extensionNumber: getHeader(extraHeaders, "X-PH-Extension") || "",
      } : null);

      if (isPowerDialerBridge) {
        const pdCallerName = extraHeaders?.["X-PH-CallerName"] || extraHeaders?.["X-Ph-Callername"] || "";
        const decodedName = decodeURIComponent((pdCallerName || "Campaign Call").replace(/\+/g, " "));
        const pdCampaignCallId = extraHeaders?.["X-PH-CampaignCallId"] || extraHeaders?.["X-Ph-Campaigncallid"] || "";
        const pdCampaignId = extraHeaders?.["X-PH-CampaignId"] || extraHeaders?.["X-Ph-Campaignid"] || "";
        const pdDid = extraHeaders?.["X-PH-DID"] || extraHeaders?.["X-Ph-Did"] || "";
        const pdFrom = extraHeaders?.["X-PH-From"] || extraHeaders?.["X-Ph-From"] || "";
        const pdQueueName = extraHeaders?.["X-PH-QueueName"] || extraHeaders?.["X-Ph-Queuename"] || "";
        const pdIvrMenuName = extraHeaders?.["X-PH-IvrMenuName"] || extraHeaders?.["X-Ph-Ivrmenuname"] || "";
        const pdCampaignName = extraHeaders?.["X-PH-CampaignName"] || extraHeaders?.["X-Ph-Campaignname"] || "";
        const pdRawCustomerNumber = pdFrom ? decodeURIComponent(pdFrom) : displayCaller;
        const isPdCallerAnonymous = isAnonymousCallerToken(pdRawCustomerNumber);
        const pdClassified = classifyCallerIdentity(pdRawCustomerNumber);
        console.log(`[Plivo] Power dialer bridge call for campaign call ${pdCampaignCallId}, contact: ${decodedName}`);

        const pdContactId = decodeURIComponent(extraHeaders?.["X-PH-ContactId"] || extraHeaders?.["X-Ph-Contactid"] || "");
        isPowerDialerCallRef.current = true;
        pdCampaignCallIdRef.current = pdCampaignCallId;
        pdCampaignIdRef.current = pdCampaignId;
        setCallState("incoming");
        setCallerName(decodedName);
        setCurrentCallUuid(callInfo?.callUUID || null);
        activeCallDirectionRef.current = "inbound";
        setCallDirection("inbound");
        // Preserve the raw customer number — do not replace with "".
        // callerKind carries the classification for display/guard purposes.
        activeCallNumberRef.current = pdRawCustomerNumber;
        setActiveCustomerNumber(pdRawCustomerNumber);
        setCallerKind(pdClassified.kind);
        setOurNumber(pdDid ? decodeURIComponent(pdDid) : "");
        setIncomingQueueName(pdQueueName ? decodeURIComponent(pdQueueName) : "");
        setIncomingIvrMenuName(pdIvrMenuName ? decodeURIComponent(pdIvrMenuName) : "");
        setIncomingCampaignName(pdCampaignName ? decodeURIComponent(pdCampaignName) : "");
        activeCallLogIdRef.current = null;
        if (pdContactId) {
          setActiveContactId(pdContactId);
          activeContactIdRef.current = pdContactId;
        }
        if (pdCampaignId) {
          setActivePdCampaignId(pdCampaignId);
        }
        if (autoAnswerEnabledRef.current && callInfo?.callUUID) {
          console.log("[Plivo] Auto-answer enabled — answering power dialer bridge call");
          answerCallRef.current?.(callInfo.callUUID);
        }
        return;
      }

      const rawDid = extraHeaders?.["X-PH-DID"] || extraHeaders?.["X-Ph-Did"] || "";
      const rawFrom = extraHeaders?.["X-PH-From"] || extraHeaders?.["X-Ph-From"] || "";
      const rawQueueName = extraHeaders?.["X-PH-QueueName"] || extraHeaders?.["X-Ph-Queuename"] || "";
      const rawIvrMenuName = extraHeaders?.["X-PH-IvrMenuName"] || extraHeaders?.["X-Ph-Ivrmenuname"] || "";
      const rawCustomerNumber = rawFrom ? decodeURIComponent(rawFrom) : displayCaller;
      // Anonymous callers: show a friendly label; clear the number so the tenant DID
      // is never displayed as the customer's identity in the dialpad.
      const isCallerAnonymous = isAnonymousCallerToken(rawCustomerNumber);
      const classified = classifyCallerIdentity(rawCustomerNumber);

      // Reset stale warm-transfer presentation state from a previous call.
      // Only safe when no active transfer is in progress for this call.
      if (!activeTransferIdRef.current) {
        setWarmTransferPhase("idle");
        setWarmTransferTarget(null);
        setIsTransferring(false);
        setTransferError(null);
      }

      setCallState("incoming");
      setCallerName(isCallerAnonymous ? "Anonymous caller" : (internalCallerName || displayCaller));
      setCurrentCallUuid(callInfo?.callUUID || null);
      const effectiveDirection = isCallbackCall ? "outbound" : "inbound";
      activeCallDirectionRef.current = effectiveDirection;
      setCallDirection(effectiveDirection);
      setIsCallbackActiveCall(isCallbackCall);
      // Preserve the raw customer number — do not replace with "".
      // callerKind carries the classification for display/guard purposes.
      activeCallNumberRef.current = rawCustomerNumber;
      setActiveCustomerNumber(rawCustomerNumber);
      setCallerKind(classified.kind);
      setOurNumber(rawDid ? decodeURIComponent(rawDid) : "");
      setIncomingQueueName(rawQueueName ? decodeURIComponent(rawQueueName) : "");
      setIncomingIvrMenuName(rawIvrMenuName ? decodeURIComponent(rawIvrMenuName) : "");

      if (autoAnswerEnabledRef.current && callInfo?.callUUID) {
        console.log("[Plivo] Auto-answer enabled — answering incoming call");
        answerCallRef.current?.(callInfo.callUUID);
      }

      if (isAutomationCall && automationContactId) {
        console.log("[Plivo] Automation call, setting contact:", automationContactId);
        setActiveContactId(automationContactId);
        activeContactIdRef.current = automationContactId;
        try {
          const contactRes = await fetch(`/api/contacts/${automationContactId}`, { credentials: "include" });
          if (contactRes.ok) {
            const contact = await contactRes.json();
            if (contact.name) setCallerName(contact.name);
          }
        } catch (e) {}
      } else if (internalCallerName) {
        console.log("[Plivo] Internal call from agent:", internalCallerName);
      } else if (!isCallerAnonymous && cleanCaller && cleanCaller !== "Unknown" && /\d{3,}/.test(cleanCaller)) {
        try {
          const contactRes = await apiRequest("POST", "/api/contacts/find-or-create", {
            phoneNumber: cleanCaller,
          });
          const contact = await contactRes.json();
          setActiveContactId(contact.id);
          if (contact.name && !/^\+?\d+$/.test(contact.name.replace(/\s/g, ""))) {
            setCallerName(contact.name);
          }
          invalidatePrefix("/api/contacts");
        } catch (e) {
          console.error("Failed to find/create contact:", e);
        }
      }

      if (isCallbackCall || isInternalCall) {
        if (isCallbackCall) {
          try {
            const res = await apiRequest("POST", "/api/call-logs", {
              direction: "inbound",
              status: "ringing",
              fromNumber: displayCaller,
              toNumber: "browser",
              callUuid: callInfo?.callUUID || null,
              contactName: null,
              duration: 0,
              startedAt: new Date().toISOString(),
              agentEndpointId: connectedEndpointIdRef.current || null,
              isChildLeg: true,
            });
            const log = await res.json();
            activeCallLogIdRef.current = log.id;
            console.log(`[Plivo] Callback/automation call — created child-leg log ${log.id}`);
          } catch (e) {
            console.error("Failed to create callback child-leg log:", e);
          }
        } else {
          activeCallLogIdRef.current = null;
        }
      } else {
        try {
          const res = await apiRequest("POST", "/api/call-logs", {
            direction: "inbound",
            status: "ringing",
            fromNumber: displayCaller,
            toNumber: "browser",
            callUuid: callInfo?.callUUID || null,
            contactName: null,
            duration: 0,
            startedAt: new Date().toISOString(),
            agentEndpointId: connectedEndpointIdRef.current || null,
          });
          const log = await res.json();
          activeCallLogIdRef.current = log.id;
        } catch (e) {
          console.error("Failed to create incoming call log:", e);
        }
      }
    });

    handle.sdk.client.on("onIncomingCallCanceled", async (callInfo: any) => {
      if (!isCurrent(handle)) return;
      // Step 1: Use callInfo.callUUID (not SDK state)
      const canceledCallUuid = callInfo?.callUUID || null;
      if (monitorCallUuidRef.current && canceledCallUuid === monitorCallUuidRef.current) {
        console.log("[Plivo] onIncomingCallCanceled for MONITOR call — clearing monitor state");
        monitorPendingRef.current = null;
        monitorCallUuidRef.current = null;
        setMonitorActive(false);
        setMonitorSessionId(null);
        setMonitorCallLogId(null);
        setMonitorMode("listen");
        setMonitorTargetName(null);
        setMonitorCallUuid(null);
        setMonitorConnectedAt(null);
        // Monitor calls don't own the main callState, but still flush in case a
        // reconnect was deferred while the agent was listening.
        flushPendingReconnect();
        return;
      }
      if (outboundDialGuardRef.current.id || !canceledCallUuid || canceledCallUuid !== incomingCallUuidRef.current) return;
      incomingCallUuidRef.current = null;
      // Capture data before clearing state.
      const canceledLogId = activeCallLogIdRef.current;
      const cancelEpId = connectedEndpointIdRef.current;

      // Immediately clear local state and flush — before any awaited I/O so a slow
      // or hung persistence call never blocks the deferred reconnect.
      activeCallLogIdRef.current = null;
      callStateRef.current = 'idle';
      resetCallState();
      flushPendingReconnect();

      // Fire-and-forget persistence.
      if (canceledLogId) {
        updateCallLog(canceledLogId, {
          status: "missed",
          endedAt: new Date().toISOString(),
        }).catch(() => {});
      }
      if (cancelEpId) {
        fetch(`/api/agent-statuses/${cancelEpId}/call-rejected`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        }).catch(() => {});
      }
    });

    handle.sdk.client.on("onCalling", (callInfo: any) => {
      if (!isCurrent(handle)) return;
      if (!acceptSdkCallEvent(outboundDialGuardRef.current, callInfo, incomingCallUuidRef.current)) return;
      console.log("[Plivo] onCalling — call initiated, UUID:", callInfo?.callUUID);
      setCallState("ringing");
    });

    handle.sdk.client.on("onCallRemoteRinging", async (callInfo: any) => {
      if (!isCurrent(handle)) return;
      if (!acceptSdkCallEvent(outboundDialGuardRef.current, callInfo, incomingCallUuidRef.current)) return;
      setCallState("ringing");
      let uuid = callInfo?.callUUID || null;
      if (!uuid) {
        try { uuid = handle.sdk.client.getCallUUID?.() || null; } catch (e) {}
      }
      if (uuid) {
        setCurrentCallUuid(uuid);
        if (activeCallLogIdRef.current) {
          await updateCallLog(activeCallLogIdRef.current, { callUuid: uuid });
        }
      }
    });

    handle.sdk.client.on("onCallAnswered", async (callInfo: any) => {
      if (!isCurrent(handle)) return;
      // Step 1: Use callInfo.callUUID
      const answeredUuid = callInfo?.callUUID || null;
      if (monitorCallUuidRef.current && answeredUuid && answeredUuid === monitorCallUuidRef.current) {
        console.log("[Plivo] onCallAnswered for MONITOR call — skipping dialpad state changes");
        setMonitorConnectedAt(Date.now());
        return;
      }
      if (!acceptSdkCallEvent(outboundDialGuardRef.current, callInfo, incomingCallUuidRef.current)) return;
      const answeredLogId = activeCallLogIdRef.current;
      const answeredAttemptId = outboundDialGuardRef.current.id;
      if (answeredAttemptId && callStartRef.current) return; // duplicate Answer must not restart duration
      setCallState("in-progress");
      setIsRecordingPaused(false); // defensive: clear any paused state from the previous call

      recoverRemoteAudio();
      if (handle.audioWatchdog) clearInterval(handle.audioWatchdog);
      handle.audioWatchdog = setInterval(recoverRemoteAudio, 3000);
      audioWatchdogRef.current = handle.audioWatchdog;

      let uuid = answeredUuid;
      if (!uuid) {
        try { uuid = handle.sdk.client.getCallUUID?.() || null; } catch (e) {}
      }
      setCurrentCallUuid(uuid);
      callStartRef.current = Date.now();
      timerRef.current = setInterval(() => {
        if (callStartRef.current) {
          setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
        }
      }, 1000);

      broadcastCallEvent("call_started", {
        callerName: activeCallNumberRef.current,
        dialedNumber: activeCallNumberRef.current,
        direction: activeCallDirectionRef.current,
      });

      if (answeredLogId) {
        const answerUpdate: Record<string, any> = { status: "in-progress" };
        if (uuid) answerUpdate.callUuid = uuid;
        await updateCallLog(answeredLogId, answerUpdate);
      }

      if (answeredAttemptId && !outboundDialGuardRef.current.owns(answeredAttemptId)) return;

      if (activeCallDirectionRef.current === "outbound" && activeCallLogIdRef.current) {
        const logId = activeCallLogIdRef.current;
        setTimeout(async () => {
          if (!isCurrent(handle)) return;
          if (answeredAttemptId && !outboundDialGuardRef.current.owns(answeredAttemptId)) return;
          try {
            const logRes = await fetch(`/api/call-logs/${logId}`, { credentials: "include" });
            if (logRes.ok) {
              const log = await logRes.json();
              if (answeredAttemptId && !outboundDialGuardRef.current.owns(answeredAttemptId)) return;
              if (log.fromNumber && log.fromNumber !== "browser") {
                setOurNumber(log.fromNumber);
              }
            }
          } catch (e) {}
        }, 750);
      }

      const epId = connectedEndpointIdRef.current;
      if (epId) {
        try {
          await apiRequest("POST", `/api/agent-statuses/${epId}/on-call`, {
            callUuid: uuid || undefined,
            callLogId: answeredLogId || undefined,
          });
          invalidatePrefix("/api/agent-statuses");
          invalidatePrefix("/api/dashboard-stats");
        } catch (e) {
          console.error("Failed to update agent status to on_call:", e);
        }
      }
    });

    handle.sdk.client.on("onCallTerminated", async (hangupInfo: any, callInfo: any) => {
      // Step 13: Durable server cleanup — use terminate-by-provider-uuid
      // Step 1: Use callInfo.callUUID, not mutable SDK state
      const terminatedCallUuid = callInfo?.callUUID || null;
      if (!isCurrent(handle)) return;
      if (!acceptSdkCallEvent(outboundDialGuardRef.current, callInfo, incomingCallUuidRef.current, true)) return;

      // Stop the audio recovery watchdog
      if (handle.audioWatchdog) {
        clearInterval(handle.audioWatchdog);
        handle.audioWatchdog = null;
      }
      audioWatchdogRef.current = null;

      if (!isCurrent(handle)) return;

      if (monitorCallUuidRef.current) {
        console.log("[Plivo] onCallTerminated for MONITOR call — clearing monitor state only");
        monitorPendingRef.current = null;
        monitorCallUuidRef.current = null;
        setMonitorActive(false);
        setMonitorSessionId(null);
        setMonitorCallLogId(null);
        setMonitorMode("listen");
        setMonitorTargetName(null);
        setMonitorCallUuid(null);
        setMonitorConnectedAt(null);
        // Monitor calls don't own the main callState, but still flush in case a
        // reconnect was deferred while the agent was listening.
        flushPendingReconnect();
        return;
      }

      const currentTransferId = activeTransferIdRef.current;
      if (currentTransferId) {
        // Req 2: if Complete already owns the terminal operation, do NOT send Cancel.
        // onCallTerminated fires because the agent leg hangs up after Complete kicks them —
        // racing this with a Cancel would generate a spurious 409 and interrupt the customer+target.
        const terminalAction = terminalTransferActionRef.current;
        if (terminalAction?.transferId === currentTransferId && terminalAction?.action === "complete") {
          console.log("[Plivo] onCallTerminated: Complete already owns terminal operation for transfer", currentTransferId, "— suppressing cancel");
          // Clear state but do NOT send Cancel
          activeTransferIdRef.current = null;
          terminalTransferActionRef.current = null;
          setWarmTransferPhase("idle");
          setWarmTransferTarget(null);
        } else {
          // Req 2: if Cancel already owns the op, still send cancel (idempotent on server)
          // but avoid if it was a duplicate — the server's claimCancelOperation handles idempotency.
          console.log("[Plivo] onCallTerminated during active warm transfer — clearing transfer state and sending cancel");
          activeTransferIdRef.current = null;
          terminalTransferActionRef.current = null;
          setWarmTransferPhase("idle");
          setWarmTransferTarget(null);
          apiRequest("POST", "/api/calls/transfer/cancel", { transferId: currentTransferId }).catch(() => {});
        }
      }

      // Always reset presentation state on call termination.
      // This handles the case where completeTransfer succeeded and cleared
      // activeTransferIdRef before onCallTerminated fired — the transfer block above
      // was skipped (currentTransferId=null), leaving warmTransferPhase="merged" stale.
      setWarmTransferPhase("idle");
      setWarmTransferTarget(null);

      const duration = callStartRef.current
        ? Math.floor((Date.now() - callStartRef.current) / 1000)
        : 0;

      const callLogId = activeCallLogIdRef.current;
      const contactId = activeContactIdRef.current;
      const callDirection = activeCallDirectionRef.current;

      // Capture PD identifiers BEFORE clearing them
      const wasPdCall = isPowerDialerCallRef.current;
      const pdCallId = pdCampaignCallIdRef.current;

      isPowerDialerCallRef.current = false;
      pdCampaignCallIdRef.current = "";
      pdCampaignIdRef.current = "";

      // Capture all required data before clearing state.
      const epId = connectedEndpointIdRef.current;
      const nowIso = new Date().toISOString();

      // Set disposition state synchronously so the dialog can appear immediately.
      if (callLogId) {
        setPendingDispositionCallLogId(callLogId);
        setPendingDispositionContactId(contactId);
        sessionStorage.setItem("pendingDispositionCallLogId", callLogId);
        if (contactId) sessionStorage.setItem("pendingDispositionContactId", contactId);
      }

      // Immediately clear local state and flush — before any awaited I/O so a slow or
      // hung persistence request never blocks the deferred SIP reconnect.
      activeCallLogIdRef.current = null;
      activeContactIdRef.current = null;
      callStateRef.current = 'idle';
      resetCallState();
      broadcastCallEvent("call_ended");
      broadcastCallEvent("status_changed");
      flushPendingReconnect();

      // Fire-and-forget all persistence — none of these can block the reconnect now.

      // Step 13: Durable cleanup via provider UUID (server-authoritative timestamp)
      if (terminatedCallUuid) {
        fetch("/api/call-logs/terminate-by-provider-uuid", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          // Field name must be `providerCallUuid` — server rejects `providerUuid` with 400.
          body: JSON.stringify({ providerCallUuid: terminatedCallUuid, duration, endedAt: nowIso }),
        }).catch(() => {});
      }

      if (callLogId) {
        const termUpdate: Record<string, any> = {
          status: "completed",
          duration,
          endedAt: nowIso,
        };
        if (terminatedCallUuid && callDirection !== "inbound") termUpdate.callUuid = terminatedCallUuid;
        updateCallLog(callLogId, termUpdate).then(() => {
          // CSAT auto-trigger after log is marked completed.
          fetch("/api/tenant-settings", { credentials: "include" }).then(settingsResp => {
            if (!settingsResp.ok) return;
            return settingsResp.json();
          }).then(settingsData => {
            const bs = settingsData?.tenantBusinessSettings;
            if (bs?.csatEnabled && bs?.csatAutoTrigger && !bs?.dispositionEnabled) {
              apiRequest("POST", "/api/csat/trigger", {
                callId: callLogId,
                triggerType: "auto",
              }).catch((err: unknown) => console.error("[CSAT] Auto-trigger failed:", err));
            }
          }).catch((csatErr: unknown) => console.error("[CSAT] Auto-trigger check failed:", csatErr));
        }).catch(() => {});
      }

      if (wasPdCall && pdCallId && !callLogId) {
        const attemptFetch = async (retriesLeft: number): Promise<void> => {
          try {
            const resp = await fetch(`/api/pd/campaign-calls/${pdCallId}/disposition-info`, {
              credentials: "include",
            });
            if (resp.ok) {
              const data = await resp.json();
              if (data.callLogId) {
                console.log(`[Plivo] PD disposition-info resolved — callLogId=${data.callLogId}`);
                setPendingDispositionCallLogId(data.callLogId);
                setPendingDispositionContactId(data.contactId || null);
                sessionStorage.setItem("pendingDispositionCallLogId", data.callLogId);
                if (data.contactId) sessionStorage.setItem("pendingDispositionContactId", data.contactId);
                return;
              }
            }
          } catch (e) {
            console.error("[Plivo] PD disposition-info fetch error:", e);
          }
          if (retriesLeft > 0) {
            console.log(`[Plivo] PD call log not ready yet, retrying… (${retriesLeft} left)`);
            setTimeout(() => attemptFetch(retriesLeft - 1), 1500);
          } else {
            console.warn("[Plivo] Could not resolve PD call log after retries — disposition dialog will not appear");
          }
        };
        setTimeout(() => attemptFetch(3), 2000);
      }

      if (epId) {
        apiRequest("POST", `/api/agent-statuses/${epId}/call-ended`, {
          direction: callDirection,
          callUuid: terminatedCallUuid || undefined,
          callLogId: callLogId || undefined,
        }).catch((e: unknown) => console.error("Failed to restore agent status after call:", e));
      }

      invalidatePrefix("/api/dashboard-stats");
      invalidatePrefix("/api/agent-statuses");
      invalidatePrefix("/api/call-logs");

      if (wasPdCall && pdCallId) {
        setTimeout(async () => {
          try {
            const dispResp = await fetch(`/api/pd/campaign-calls/${pdCallId}/disposition-info`, { credentials: "include" });
            if (!dispResp.ok) return;
            const dispData = await dispResp.json();
            if (!dispData.callLogId) return;
            const logResp = await fetch(`/api/call-logs/${dispData.callLogId}`, { credentials: "include" });
            if (!logResp.ok) return;
            const log = await logResp.json();
            const STATUS_TO_CAUSE: Record<string, string> = { "no-answer": "NO_ANSWER", "busy": "USER_BUSY" };
            const SILENT_HC = new Set(["NORMAL_CLEARING", "ORIGINATOR_CANCEL"]);
            if (log.hangupCause && !SILENT_HC.has(log.hangupCause)) {
              setLastCallEndReason(log.hangupCause);
            } else if (STATUS_TO_CAUSE[log.status]) {
              setLastCallEndReason(STATUS_TO_CAUSE[log.status]);
            }
          } catch {}
        }, 2500);
      }
    });

    handle.sdk.client.on("onCallFailed", async (cause: string, callInfo: any) => {
      // Step 1: Use callInfo.callUUID
      const failedCallUuid = callInfo?.callUUID || null;
      if (!isCurrent(handle)) return;
      if (!acceptSdkCallEvent(outboundDialGuardRef.current, callInfo, incomingCallUuidRef.current, true)) return;

      if (!isCurrent(handle)) return;

      if (monitorCallUuidRef.current) {
        console.log(`[Plivo] onCallFailed for MONITOR call (${cause}) — clearing monitor state only`);
        monitorPendingRef.current = null;
        monitorCallUuidRef.current = null;
        setMonitorActive(false);
        setMonitorSessionId(null);
        setMonitorCallLogId(null);
        setMonitorMode("listen");
        setMonitorTargetName(null);
        setMonitorCallUuid(null);
        setMonitorConnectedAt(null);
        // Monitor calls don't own the main callState, but still flush in case a
        // reconnect was deferred while the agent was listening.
        flushPendingReconnect();
        return;
      }

      const NOTIFIABLE_CAUSES = new Set([
        "UNALLOCATED_NUMBER", "NO_ROUTE_DESTINATION", "NO_ROUTE_TRANSIT_NET",
        "NO_ANSWER", "USER_BUSY", "CALL_REJECTED",
      ]);
      const SILENT_CAUSES = new Set(["ORIGINATOR_CANCEL", "NORMAL_CLEARING"]);
      const normalisedCause = (cause || "").toUpperCase().replace(/[\s-]+/g, "_").trim();
      if (!SILENT_CAUSES.has(normalisedCause)) {
        setLastCallEndReason(normalisedCause);
      }

      // Capture data before clearing state.
      const failedCallLogId = activeCallLogIdRef.current;
      const failedAttemptId = outboundDialGuardRef.current.id;
      const callDirectionFailed = activeCallDirectionRef.current;
      const epId = connectedEndpointIdRef.current;

      // Immediately clear local state and flush — before any awaited I/O.
      activeCallLogIdRef.current = null;
      callStateRef.current = 'idle';
      resetCallState();
      broadcastCallEvent("call_ended");
      broadcastCallEvent("status_changed");
      flushPendingReconnect();

      // Fire-and-forget persistence.
      if (failedAttemptId) {
        fetch("/api/outbound-call-context/cancel-pending", {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ outboundAttemptId: failedAttemptId, pendingCallLogId: failedCallLogId || undefined, notes: "sdk_call_failed" }),
        }).catch(() => {});
      }
      if (failedCallLogId) {
        const failUpdate: Record<string, any> = {
          status: "failed",
          endedAt: new Date().toISOString(),
        };
        if (failedCallUuid) failUpdate.callUuid = failedCallUuid;
        updateCallLog(failedCallLogId, failUpdate).catch(() => {});

        if (!SILENT_CAUSES.has(normalisedCause)) {
          setTimeout(async () => {
            try {
              const logResp = await fetch(`/api/call-logs/${failedCallLogId}`, { credentials: "include" });
              if (!logResp.ok) return;
              const log = await logResp.json();
              const SILENT_HC = new Set(["NORMAL_CLEARING", "ORIGINATOR_CANCEL"]);
              if (log.hangupCause && !SILENT_HC.has(log.hangupCause) && log.hangupCause !== normalisedCause) {
                setLastCallEndReason(log.hangupCause);
              }
            } catch {}
          }, 2500);
        }
      }
      if (epId) {
        apiRequest("POST", `/api/agent-statuses/${epId}/call-ended`, {
          direction: callDirectionFailed,
          callUuid: failedCallUuid || undefined,
          callLogId: failedCallLogId || undefined,
        }).catch((e: unknown) => console.error("Failed to restore agent status after failed call:", e));
      }
      invalidatePrefix("/api/dashboard-stats");
      invalidatePrefix("/api/agent-statuses");
      invalidatePrefix("/api/call-logs");
    });

    handle.sdk.client.on("onMediaConnected", () => {
      if (!isCurrent(handle)) return;
      console.log("[Plivo] onMediaConnected — WebRTC media tracks active");
      recoverRemoteAudio();
      if (handle.audioWatchdog === null) {
        handle.audioWatchdog = setInterval(recoverRemoteAudio, 3000);
        audioWatchdogRef.current = handle.audioWatchdog;
      }
    });

    handle.sdk.client.on("mediaMetrics", (metrics: any) => {
      if (!isCurrent(handle)) return;
      const { MOS, jitter, packetLoss, audioReceived } = metrics || {};
      console.log(`[Plivo] mediaMetrics — MOS: ${MOS ?? "n/a"}, jitter: ${jitter ?? "n/a"}ms, packetLoss: ${packetLoss ?? "n/a"}%, audioReceived: ${audioReceived}`);
      if (typeof MOS === "number" && MOS < 3.0) {
        console.warn("[Plivo] Low MOS score detected — triggering audio recovery");
        recoverRemoteAudio();
      }
    });

    handle.sdk.client.on("onConnectionChange", (event: any) => {
      if (!isCurrent(handle)) return;
      console.log("[Plivo] Connection change event:", JSON.stringify(event));
      if (event?.state === "disconnected" || event?.state === "failed") {
        console.log("[Plivo] WebSocket disconnected, marking as unregistered");
        setIsRegistered(false);
      } else if (event?.state === "connected") {
        console.log("[Plivo] WebSocket reconnected");
      }
    });

    handle.sdk.client.on("onLogout", () => {
      if (!isCurrent(handle)) return;
      console.log("[Plivo] SDK logged out (unexpected), marking as unregistered for re-login");
      setIsRegistered(false);
    });
  }

  // ─── SDK-instance lifetime effect ─────────────────────────────────────────────
  // Creates the SDK instance once (on mount) and tears it down on unmount.
  // connectHandle() is called externally by autoConnect() / requestSipReconnect().
  useEffect(() => {
    if (typeof window.Plivo === "undefined") return;

    const handle = createSdkInstance();
    bindSdkInstanceEvents(handle);

    // Step 11: Check SIP health on visibility/online events
    const handleOnline = () => { checkSipHealthMe().catch(() => {}); };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkSipHealthMe().catch(() => {});
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timerRef.current) clearInterval(timerRef.current);
      // acwTimerRef removed — server handles ACW restoration atomically
      if (sipRecoverRetryTimerRef.current) clearTimeout(sipRecoverRetryTimerRef.current);
      disposeSdkInstance(handle);
      currentSdkHandleRef.current = null;
      plivoRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Step 9: sip-reregister in PlivoProvider (moved from App.tsx) ──────────────
  useEffect(() => {
    const tenantId = (user as any)?.impersonating?.tenantId || (user as any)?.tenantId;
    if (!tenantId) return;
    const channel = getTenantChannel(tenantId);
    if (!channel) return;

    const handleSipReregister = (payload: SipReregisterPayload) => {
      if (payload.endpointId && payload.endpointId !== connectedEndpointIdRef.current) return;
      console.log("[Plivo] sip-reregister signal received from server — initiating reconnect");
      if (payload.healthEpisodeId) {
        healthEpisodeIdRef.current = payload.healthEpisodeId;
      }
      requestSipReconnect(payload.healthEpisodeId || null);
    };

    channel.bind("sip-reregister", handleSipReregister);

    // ── Transfer cancel Pusher events ────────────────────────────────────────────
    // Req 4: Pusher and poll call the same idempotent finalizer.
    // phase=cancelled alone is NOT sufficient — require customerReconnected=true.
    const handleTransferCancelled = (payload: any) => {
      const tid = activeTransferIdRef.current;
      if (!tid || payload.transferId !== tid) return;
      console.log("[Plivo] transfer-cancelled Pusher event for", tid, payload);
      // Finalize only when conference-enter has confirmed reconnect
      if (payload.customerReconnected === true) {
        finalizeCancelTransfer(tid, false);
      } else {
        // phase=cancelled received but customerReconnected not yet confirmed.
        // Keep polling — conference-enter event will flip customerReconnected=true
        // and emit another transfer-cancelled with the flag set.
        console.log("[Plivo] transfer-cancelled received but customerReconnected=false — keeping poll alive");
      }
    };

    const handleTransferCancelReconnectFailed = (payload: any) => {
      const tid = activeTransferIdRef.current;
      if (!tid || payload.transferId !== tid) return;
      console.log("[Plivo] transfer-cancel-reconnect-failed Pusher event for", tid, payload);
      // Customer could not be reconnected — finalize with error so agent can hang up
      finalizeCancelTransfer(tid, true);
    };

    channel.bind("transfer-cancelled", handleTransferCancelled);
    channel.bind("transfer-cancel-reconnect-failed", handleTransferCancelReconnectFailed);

    return () => {
      channel.unbind("sip-reregister", handleSipReregister);
      channel.unbind("transfer-cancelled", handleTransferCancelled);
      channel.unbind("transfer-cancel-reconnect-failed", handleTransferCancelReconnectFailed);
      releaseTenantChannel(tenantId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(user as any)?.tenantId, (user as any)?.impersonating?.tenantId]);

  // ─── Step 11: Pusher reconnect → check SIP health ─────────────────────────────
  useEffect(() => {
    const unsub = subscribeToReconnect(() => {
      checkSipHealthMe().catch(() => {});
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Reset call state ─────────────────────────────────────────────────────────
  const resetCallState = useCallback(() => {
    const outboundId = outboundDialGuardRef.current.id;
    if (outboundId) outboundDialGuardRef.current.release(outboundId);
    incomingCallUuidRef.current = null;
    setCallState("idle");
    setCurrentCallUuid(null);
    setCallerName("");
    setCallDuration(0);
    setIsMuted(false);
    setIsOnHold(false);
    setIsRecordingPaused(false);
    setActiveContactId(null);
    setActiveCustomerNumber("");
    setCallerKind('anonymous');
    setOurNumber("");
    setIncomingInternalCall(null);
    setIsCallbackActiveCall(false);
    setIncomingQueueName("");
    setIncomingIvrMenuName("");
    setIncomingCampaignName("");
    setActivePdCampaignId(null);
    activeCallDirectionRef.current = "outbound";
    activeTransferIdRef.current = null;
    setIsTransferring(false);
    setWarmTransferPhase("idle");
    setWarmTransferTarget(null);
    callStartRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Clear warm-transfer refs
    if (cancelPollIntervalRef.current) {
      clearInterval(cancelPollIntervalRef.current);
      cancelPollIntervalRef.current = null;
    }
    holdInProgressRef.current = false;
    wasMutedBeforeHoldRef.current = false;
    remoteAudioIntentionallyMutedRef.current = false;
    preTransferManualMuteRef.current = false;
  }, []);

  const clearPendingDisposition = useCallback(() => {
    setPendingDispositionCallLogId(null);
    setPendingDispositionContactId(null);
    sessionStorage.removeItem("pendingDispositionCallLogId");
    sessionStorage.removeItem("pendingDispositionContactId");
  }, []);

  // ─── login: route through the locked connectHandle infrastructure ──────────────
  const login = useCallback((username: string, password: string, endpointId?: string) => {
    const handle = currentSdkHandleRef.current;
    if (!handle || handle.disposed) {
      setRegistrationError("Plivo SDK not initialized. Please refresh the page.");
      return;
    }
    if (endpointId) {
      setConnectedEndpointId(endpointId);
      connectedEndpointIdRef.current = endpointId;
    }
    setRegistrationError(null);
    if (handle.loginInProgress) return;
    handle.loginInProgress = true;
    // Mark as manual login so onLogin applies statusAfterLogin.
    handle.reason = 'manual';

    console.log("[Plivo] Attempting login with username:", username, "password length:", password.length);
    const accepted = handle.sdk.client.login(username, password);
    if (accepted === false) {
      handle.loginInProgress = false;
      incrementFailureCounter();
      maybeEscalate();
      scheduleRetry(handle, nextBackoffDelay());
      return;
    }
    // Start handshake timer — cleared by onLogin / onLoginFailed
    handle.handshakeTimer = setTimeout(() => {
      handle.handshakeTimer = null;
      if (!isCurrent(handle)) return;
      console.warn("[Plivo] Handshake timeout (login path) — replacing SDK instance");
      incrementFailureCounter();
      maybeEscalate();
      replaceAndConnect('automatic_retry');
    }, 20_000);
  }, []);

  // ─── autoConnect: delegate to connectHandle ────────────────────────────────────
  const autoConnect = useCallback(async () => {
    const handle = currentSdkHandleRef.current;
    if (!handle || handle.disposed) {
      console.log("[Plivo] autoConnect: SDK handle not ready");
      return;
    }
    await connectHandle(handle);
  }, []);

  // ─── disconnect ────────────────────────────────────────────────────────────────
  const disconnect = useCallback(async () => {
    const epId = connectedEndpointId;
    if (epId) {
      try {
        const settingsRes = await fetch("/api/tenant-settings", { credentials: "include" });
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          const logoutStatus = settingsData?.tenantBusinessSettings?.statusAfterLogout || "offline";
          const SYSTEM_STATUSES = ["ready", "not_ready", "on_call", "after_call_work", "offline"];
          const isSystem = SYSTEM_STATUSES.includes(logoutStatus);
          await apiRequest("PUT", `/api/agent-statuses/${epId}`, {
            status: isSystem ? logoutStatus : "not_ready",
            customLabel: isSystem ? null : logoutStatus,
          });
        }
      } catch (err) {
        console.error("[Plivo] Failed to set status after logout:", err);
      }
    }
    const handle = currentSdkHandleRef.current;
    if (handle && !handle.disposed) {
      disposeSdkInstance(handle);
      // Re-create a fresh handle so the SDK is ready for next login
      if (typeof window.Plivo !== "undefined") {
        const newHandle = createSdkInstance();
        bindSdkInstanceEvents(newHandle);
      }
    }
    setIsRegistered(false);
    setConnectedEndpointId(null);
    setRegistrationError(null);
  }, [connectedEndpointId]);

  // ─── Step 9: requestSipReconnect ──────────────────────────────────────────────
  const requestSipReconnect = useCallback((healthEpisodeId: string | null) => {
    if (healthEpisodeId) healthEpisodeIdRef.current = healthEpisodeId;
    console.log("[Plivo] requestSipReconnect called — replacing SDK instance (reason=health_recovery)");
    replaceAndConnect('health_recovery').catch(() => {});
  }, []);

  // ─── makeCall ─────────────────────────────────────────────────────────────────
  const makeCall = useCallback(async (number: string, callerId?: string, campaignId?: string, displayCallerId?: string) => {
    if (isDispositionBlocked) return;
    const outboundAttemptId = crypto.randomUUID();
    // This must precede microphone permissions, DNC lookup and every await.
    if (!outboundDialGuardRef.current.acquire(outboundAttemptId, callStateRef.current === "idle" && !incomingCallUuidRef.current)) return;
    let sdkStarted = false;
    let prepared = false;
    let pendingCallLogId: string | undefined;
    const stillOwnsAttempt = () => outboundDialGuardRef.current.owns(outboundAttemptId);
    const cancelLocalPreparation = () => {
      if (!stillOwnsAttempt()) return;
      outboundDialGuardRef.current.release(outboundAttemptId);
      activeCallLogIdRef.current = null;
      callStateRef.current = "idle";
      resetCallState();
      fetch("/api/outbound-call-context/cancel-pending", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ outboundAttemptId, pendingCallLogId, notes: "browser_preparation_expired" }),
      }).catch(() => {});
    };
    const checkPreparation = async () => {
      if (!stillOwnsAttempt() || outboundDialGuardRef.current.bound) return;
      if (!sdkStarted) { cancelLocalPreparation(); return; }
      try {
        const response = await fetch(`/api/outbound-call-context/${outboundAttemptId}`, { credentials: "include" });
        if (!response.ok) throw new Error("Unable to check call preparation");
        const state = await response.json();
        if (!stillOwnsAttempt()) return;
        if (state.phase === "ended") {
          try { plivoRef.current?.client?.hangup(); } catch {}
          cancelLocalPreparation();
          return;
        }
        if (state.phase === "active" && typeof state.callUuid === "string" && state.callUuid) {
          outboundDialGuardRef.current.accept(state.callUuid, false, outboundAttemptId);
          return;
        }
      } catch {}
      if (stillOwnsAttempt()) setTimeout(checkPreparation, 2000);
    };
    const preparationTimer = setTimeout(checkPreparation, OUTBOUND_PREPARATION_TIMEOUT_MS);
    try {

    try {
      let micDenied = false;
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
        if (!stillOwnsAttempt()) return;
        if (result.state === "denied") micDenied = true;
      }
      if (!micDenied) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
          if (!stillOwnsAttempt()) return;
        } catch (micErr: any) {
          const name = micErr?.name || "";
          if (name === "NotAllowedError" || name === "PermissionDeniedError") micDenied = true;
        }
      }
      if (micDenied) {
        setMicBlockedMode("outbound");
        setMicBlockedDialogOpen(true);
        return;
      }
    } catch (e) {
      console.warn("[makeCall] Could not check mic permission:", e);
    }
    if (!stillOwnsAttempt()) return;

    // Reset stale warm-transfer presentation state from a previous call.
    if (!activeTransferIdRef.current) {
      setWarmTransferPhase("idle");
      setWarmTransferTarget(null);
      setIsTransferring(false);
      setTransferError(null);
    }

    if (plivoRef.current && isRegistered) {
      const isExtension = /^\d{3,4}$/.test(number);
      if (!isExtension) {
        try {
          const dncRes = await fetch(`/api/dnc/check?phoneNumber=${encodeURIComponent(number)}`);
          if (dncRes.ok) {
            const dncData = await dncRes.json();
            if (!stillOwnsAttempt()) return;
            if (dncData.doNotCall) {
              setDncWarning({ type: "call", contactName: dncData.contactName });
              return;
            }
          }
        } catch (e) {
          console.error("DNC check failed:", e);
        }
      }

      // ── Unique ID for this dial attempt ─────────────────────────────────────
      // Generated before the first context request so every abort path can
      // reference it, even when no call-log row has been created yet.
      let resolvedCallerId: string | null;

      /** Cancel a pending call-log row and clear the Map context atomically.
       *  Safe to call at any abort point — server guards prevent double-cancel. */
      const abortPreparedCall = async (
        pendingCallLogId: string | undefined,
        notes: string
      ): Promise<void> => {
        try {
          await fetch("/api/outbound-call-context/cancel-pending", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ outboundAttemptId, pendingCallLogId, notes }),
          });
        } catch (e) {
          console.warn("[makeCall] abortPreparedCall fetch error (non-fatal):", e);
        }
      };

      // ── Step 1: PREPARE — billing gate ──────────────────────────────────────
      try {
        // A lost response does not prove the server failed to reserve the ID.
        prepared = true;
        const prepareRes = await fetch("/api/outbound-call-context", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            outboundAttemptId,
            selectedCallerId: callerId || undefined,
            campaignId: campaignId || undefined,
            destinationNumber: number,
          }),
        });
        const preparePayload: unknown = await prepareRes.json().catch(() => null);
        if (prepareRes.status === 402) {
          toast({
            title: "Insufficient balance",
            description: readApiMessage(
              preparePayload,
              "Your account has insufficient balance to make calls. Please top up your account.",
            ),
            variant: "destructive",
          });
          return; // No log row created yet — nothing to cancel.
        }
        if (!prepareRes.ok) {
          console.error("[makeCall] PREPARE failed status:", prepareRes.status);
          toast({
            title: "Call not started",
            description: readApiMessage(
              preparePayload,
              "Unable to resolve an eligible outbound Caller ID.",
            ),
            variant: "destructive",
          });
          return;
        }
        resolvedCallerId = readResolvedCallerId(preparePayload);
        prepared = true;
      } catch (e) {
        console.error("[makeCall] PREPARE network error:", e);
        toast({
          title: "Call not started",
          description: "Unable to validate the outbound Caller ID. Please try again.",
          variant: "destructive",
        });
        return;
      }
      if (!stillOwnsAttempt()) return;

      if (!isExtension && !resolvedCallerId) {
        toast({
          title: "Call not started",
          description: "No eligible outbound Caller ID is available.",
          variant: "destructive",
        });
        return;
      }

      // ── Immediate ringing feedback ───────────────────────────────────────
      // PREPARE passed — the call is definitely going ahead. Set ringing NOW
      // so the floating dialpad and status pill respond instantly rather than
      // waiting for the Plivo SDK's onCalling event (which only fires after
      // the async log-creation + BIND steps and client.call() are complete).
      // Every failure path below already calls resetCallState() which resets
      // this back to "idle" if something goes wrong.
      callStateRef.current = "ringing";
      setCallState("ringing");

      // ── Step 2: Resolve UI state + extension label ────────────────────────
      setLastCallEndReason(null);
      setDialedNumber(number);
      setCallerName(number);
      setIncomingInternalCall(null);
      activeCallDirectionRef.current = "outbound";
      setCallDirection("outbound");
      activeCallNumberRef.current = number;
      setActiveCustomerNumber(number);
      setOurNumber(resolvedCallerId || displayCallerId || "");

      // Resolve/create the contact for this number so screen pop (and the
      // active-call indicator on the Contact page) has an activeContactId to
      // act on — mirrors the inbound find-or-create flow above. Extensions
      // (internal calls) are excluded, same as inbound internal calls.
      if (!isExtension && /\d{3,}/.test(number)) {
        try {
          const contactRes = await apiRequest("POST", "/api/contacts/find-or-create", {
            phoneNumber: number,
          });
          const contact = await contactRes.json();
          if (!stillOwnsAttempt()) return;
          setActiveContactId(contact.id);
          activeContactIdRef.current = contact.id;
          if (contact.name && !/^\+?\d+$/.test(contact.name.replace(/\s/g, ""))) {
            setCallerName(contact.name);
          }
          invalidatePrefix("/api/contacts");
        } catch (e) {
          console.error("[makeCall] Failed to find/create contact:", e);
        }
      }

      let extensionLabel: string | null = null;
      if (isExtension) {
        try {
          const extRes = await fetch("/api/extensions");
          if (extRes.ok) {
            const exts = await extRes.json();
            const matchingExt = exts.find((e: any) => e.extensionNumber === number);
            if (matchingExt?.label) {
              extensionLabel = `Ext ${number} - ${matchingExt.label}`;
            }
          }
        } catch (e) {}
      }

      // ── Step 3: Create pending call-log row ──────────────────────────────
      const callLogData: Record<string, any> = {
        outboundAttemptId,
        direction: "outbound",
        status: "ringing",
        fromNumber: resolvedCallerId || "browser",
        toNumber: number,
        callUuid: null,
        contactName: null,
        duration: 0,
        startedAt: new Date().toISOString(),
        agentEndpointId: connectedEndpointIdRef.current || null,
      };
      if (campaignId) callLogData.campaignId = campaignId;
      if (extensionLabel) callLogData.contactName = extensionLabel;

      if (!stillOwnsAttempt()) return;
      try {
        const logRes = await apiRequest("POST", "/api/call-logs", callLogData);
        const log = await logRes.json();
        pendingCallLogId = log.id;
        if (!stillOwnsAttempt()) return;
        activeCallLogIdRef.current = log.id;
      } catch (e) {
        console.error("[makeCall] Failed to create outbound call log:", e);
        // Log row was never created — only clear the Map context.
        await abortPreparedCall(undefined, "log_create_failed");
        if (!stillOwnsAttempt()) return;
        callStateRef.current = "idle";
        resetCallState();
        flushPendingReconnect();
        return;
      }

      // ── Step 4: BIND — attach log ID to context ──────────────────────────
      try {
        const bindRes = await fetch("/api/outbound-call-context", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            outboundAttemptId,
            selectedCallerId: resolvedCallerId || undefined,
            campaignId: campaignId || undefined,
            pendingCallLogId,
            destinationNumber: number,
          }),
        });
        if (!stillOwnsAttempt()) return;
        if (bindRes.status === 402) {
          await abortPreparedCall(pendingCallLogId, "no_credits");
          if (!stillOwnsAttempt()) return;
          toast({
            title: "Insufficient balance",
            description: "Your account has insufficient balance to make calls. Please top up your account.",
            variant: "destructive",
          });
          callStateRef.current = "idle";
          resetCallState();
          flushPendingReconnect();
          return;
        }
        if (bindRes.status === 409) {
          // Another attempt already owns this SIP username — abort cleanly.
          await abortPreparedCall(pendingCallLogId, "attempt_changed");
          if (!stillOwnsAttempt()) return;
          toast({
            title: "Call not started",
            description: "Another outbound attempt is already active.",
            variant: "destructive",
          });
          callStateRef.current = "idle";
          resetCallState();
          flushPendingReconnect();
          return;
        }
        if (!bindRes.ok) {
          // Transient 5xx — continue. X-PH-CallLogId in the SIP header is authoritative.
          console.warn("[makeCall] BIND transient failure status:", bindRes.status, "— continuing via X-PH-CallLogId");
        }
      } catch (e) {
        // Network error — continue. X-PH-CallLogId covers this.
        console.warn("[makeCall] BIND network error (non-fatal, continuing via X-PH-CallLogId):", e);
      }
      if (!stillOwnsAttempt()) return;

      // ── Step 5: Dial ─────────────────────────────────────────────────────
      const extraHeaders: Record<string, string> = {};
      if (resolvedCallerId) extraHeaders["X-PH-CallerID"] = resolvedCallerId;
      if (campaignId) extraHeaders["X-PH-CampaignId"] = campaignId;
      // Cross-instance, restart-safe correlation key — authoritative over the Map.
      if (pendingCallLogId) extraHeaders["X-PH-CallLogId"] = pendingCallLogId;
      extraHeaders["X-PH-OutboundAttemptId"] = outboundAttemptId;

      let callResult: any;
      try {
        outboundDialGuardRef.current.markStarted(outboundAttemptId);
        callResult = plivoRef.current.client.call(number, extraHeaders);
        sdkStarted = callResult !== false;
      } catch (sdkErr) {
        console.error("[Plivo] client.call() threw:", sdkErr);
        await abortPreparedCall(pendingCallLogId, "sdk_threw");
        if (!stillOwnsAttempt()) return;
        toast({
          title: "Call failed",
          description: "Unable to initiate call. Please check your connection and try again.",
          variant: "destructive",
        });
        callStateRef.current = "idle";
        resetCallState();
        flushPendingReconnect();
        return;
      }

      if (callResult === false) {
        console.error("[Plivo] client.call() returned false — SDK rejected the call");
        await abortPreparedCall(pendingCallLogId, "sdk_rejected");
        if (!stillOwnsAttempt()) return;
        toast({
          title: "Call failed",
          description: "Unable to initiate call. Please check your connection and try again.",
          variant: "destructive",
        });
        callStateRef.current = "idle";
        resetCallState();
        flushPendingReconnect();
        return;
      }

      // Ownership comes from this attempt's SDK events or its server record.
      // The SDK's mutable current-UUID getter can still point at an earlier call.
    }
    } finally {
      if (!sdkStarted) {
        clearTimeout(preparationTimer);
        if (prepared) {
          // Covers cancellation while awaiting contact/log creation and PREPARE
          // responses, including a response arriving after the user hung up.
          fetch("/api/outbound-call-context/cancel-pending", {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ outboundAttemptId, pendingCallLogId, notes: "browser_attempt_aborted" }),
          }).catch(() => {});
        }
        if (stillOwnsAttempt()) {
          activeCallLogIdRef.current = null;
          outboundDialGuardRef.current.release(outboundAttemptId);
          callStateRef.current = "idle";
          resetCallState();
        }
      }
    }
  }, [isRegistered, isDispositionBlocked]);

  const answerCall = useCallback(async (overrideUuid?: string) => {
    setLastCallEndReason(null);
    try {
      let micDenied = false;
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
        if (result.state === "denied") micDenied = true;
      }
      if (!micDenied) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        } catch (micErr: any) {
          const name = micErr?.name || "";
          if (name === "NotAllowedError" || name === "PermissionDeniedError") micDenied = true;
        }
      }
      if (micDenied) {
        const callUuid = plivoRef.current?.client?.callUUID || null;
        if (plivoRef.current) {
          plivoRef.current.client.reject();
        }
        const epId = connectedEndpointIdRef.current;
        if (epId) {
          try {
            await apiRequest("POST", "/api/call-rejections", {
              callUuid,
              callLogId: activeCallLogIdRef.current || null,
              agentEndpointId: epId,
              direction: "inbound",
            });
          } catch (_e) {}
          try {
            await apiRequest("POST", `/api/agent-statuses/${epId}/call-rejected`, {});
          } catch (_e) {}
          try {
            await apiRequest("PUT", `/api/agent-statuses/${epId}`, { status: "not_ready" });
            invalidatePrefix("/api/agent-statuses");
            invalidatePrefix("/api/dashboard-stats");
          } catch (_e) {}
        }
        // Mic was denied — reject path. Reset call state and flush any pending reconnect.
        activeCallLogIdRef.current = null;
        callStateRef.current = 'idle';
        resetCallState();
        flushPendingReconnect();
        setMicBlockedDialogOpen(true);
        return;
      }
    } catch (e) {
      console.warn("[answerCall] Could not check mic permission:", e);
    }

    if (pendingDispositionCallLogId) {
      setPendingDispositionCallLogId(null);
      setPendingDispositionContactId(null);
      sessionStorage.removeItem("pendingDispositionCallLogId");
      sessionStorage.removeItem("pendingDispositionContactId");
    }
    const uuid = overrideUuid ?? currentCallUuid;
    if (callStateRef.current !== "ringing" && callStateRef.current !== "incoming") {
      console.log("[answerCall] Call no longer ringing — customer likely hung up, skipping answer");
      return;
    }
    if (plivoRef.current && uuid) {
      plivoRef.current.client.answer(uuid);
    }
  }, [currentCallUuid, pendingDispositionCallLogId, toast]);

  const answerCallRef = useRef<((overrideUuid?: string) => void) | null>(null);
  useEffect(() => {
    answerCallRef.current = answerCall;
  });

  const hangupCall = useCallback(async () => {
    // Cancel ownership immediately, including while microphone/PREPARE/contact
    // requests are pending. Their continuations must not dial after this point.
    const dialingAttemptId = outboundDialGuardRef.current.id;
    if (dialingAttemptId && !outboundDialGuardRef.current.started) {
      fetch("/api/outbound-call-context/cancel-pending", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ outboundAttemptId: dialingAttemptId, pendingCallLogId: activeCallLogIdRef.current || undefined, notes: "browser_preparation_cancelled" }),
      }).catch(() => {});
      outboundDialGuardRef.current.release(dialingAttemptId);
      activeCallLogIdRef.current = null;
      callStateRef.current = "idle";
      resetCallState();
      return;
    }
    const sdkClient = plivoRef.current?.client;
    if (!sdkClient) return;

    const callLogId = activeCallLogIdRef.current;
    const agentProviderCallUuid = sdkClient.callUUID || currentCallUuid || null;
    const shouldHangupInboundCustomer =
      activeCallDirectionRef.current === "inbound" &&
      (!!callLogId || !!agentProviderCallUuid) &&
      !activeTransferIdRef.current;

    if (shouldHangupInboundCustomer) {
      // The browser SDK only ends the agent SIP leg. Ask the server to hang up
      // the separate inbound customer leg first, using the canonical UUID on the
      // call log. Bound the wait so a slow provider request never traps the agent
      // in the call UI; the normal SDK hangup still runs in finally.
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 2000);
      try {
        const response = await fetch("/api/calls/hangup-inbound-customer", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify({
            callLogId: callLogId || undefined,
            agentProviderCallUuid,
          }),
        });
        if (!response.ok && response.status !== 409) {
          console.error(`[Plivo] Server customer-leg hangup failed with ${response.status}`);
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("[Plivo] Server customer-leg hangup request failed:", error);
        }
      } finally {
        window.clearTimeout(timeout);
      }
    }

    try {
      sdkClient.hangup();
    } catch (error) {
      console.error("[Plivo] Agent-leg hangup failed:", error);
    }
  }, [currentCallUuid]);

  const rejectCall = useCallback(async () => {
    const callUuid = plivoRef.current?.client?.callUUID || null;
    if (plivoRef.current) {
      plivoRef.current.client.reject();
    }
    const epId = connectedEndpointIdRef.current;
    if (epId) {
      try {
        await apiRequest("POST", "/api/call-rejections", {
          callUuid,
          callLogId: activeCallLogIdRef.current || null,
          agentEndpointId: epId,
          direction: activeCallDirectionRef.current || "inbound",
        });
      } catch (err) {
        console.error("[Plivo] Failed to record call rejection:", err);
      }
      try {
        sessionStorage.removeItem("conv_tab_prev_status");
        sessionStorage.removeItem("conv_tab_prev_label");
        await apiRequest("PUT", `/api/agent-statuses/${epId}`, { status: "not_ready" });
        invalidatePrefix("/api/agent-statuses");
      } catch (err) {
        console.error("[Plivo] Failed to set not_ready after reject:", err);
      }
    }
    activeCallLogIdRef.current = null;
    callStateRef.current = 'idle';
    resetCallState();
    flushPendingReconnect();
  }, []);

  const [isRemoteAudioMuted, setRemoteAudioMuted] = useState(false);

  const toggleMute = useCallback(() => {
    if (isOnHold) return;
    if (plivoRef.current) {
      if (isMuted) {
        plivoRef.current.client.unmute();
        setIsMuted(false);
      } else {
        plivoRef.current.client.mute();
        setIsMuted(true);
      }
    }
  }, [isMuted, isOnHold]);

  const toggleHold = useCallback(async () => {
    const callLogId = activeCallLogIdRef.current;
    if (!callLogId) return;
    if (holdInProgressRef.current) return; // prevent concurrent hold/unhold races
    holdInProgressRef.current = true;
    try {
      if (isOnHold) {
        try {
          await apiRequest("POST", `/api/calls/${callLogId}/unhold`, {});
        } catch (e) {
          console.error("[Plivo] Unhold failed:", e);
          // Don't update UI state — leave on-hold UI intact so the user can retry
          return;
        }
        // Restore mic to the state it was in before hold was activated
        if (plivoRef.current) {
          if (wasMutedBeforeHoldRef.current) {
            plivoRef.current.client.mute();
          } else {
            plivoRef.current.client.unmute();
            setIsMuted(false);
          }
        }
        remoteAudioIntentionallyMutedRef.current = false;
        setRemoteAudioMuted(false);
        setIsOnHold(false);
      } else {
        // Snapshot current mic state so we can restore it after unhold
        wasMutedBeforeHoldRef.current = isMuted;
        try {
          await apiRequest("POST", `/api/calls/${callLogId}/hold`, {});
        } catch (e) {
          console.error("[Plivo] Hold failed:", e);
          return;
        }
        if (plivoRef.current) {
          plivoRef.current.client.mute();
          setIsMuted(true);
        }
        remoteAudioIntentionallyMutedRef.current = true;
        // Mute the actual remote media element so the agent cannot hear the customer.
        // This must happen AFTER setting remoteAudioIntentionallyMutedRef to prevent
        // the audio watchdog from immediately unmuting it on the next 3-second tick.
        const plivoAudio = plivoRef.current?.client?.audio;
        const remoteEl: HTMLVideoElement | HTMLAudioElement | null =
          (document.getElementById("plivo_webrtc_remoteview") as HTMLVideoElement | null) ??
          (plivoAudio?.remoteAudio as HTMLAudioElement | undefined) ?? null;
        if (remoteEl) {
          remoteEl.muted = true;
          console.log("[Plivo] Hold: muted remote audio element");
        }
        setRemoteAudioMuted(true);
        setIsOnHold(true);
      }
    } finally {
      holdInProgressRef.current = false;
    }
  }, [isOnHold, isMuted, setRemoteAudioMuted]);

  const pauseRecording = useCallback(async () => {
    const callLogId = activeCallLogIdRef.current;
    if (!callLogId || recordingCommandInProgressRef.current) return;
    recordingCommandInProgressRef.current = true;
    try {
      const response = await fetch(`/api/calls/${callLogId}/recording/pause`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const payload = parseRecordingCaptureCommandResult(await response.json().catch(() => null));
      if (!response.ok || payload?.success !== true || payload.paused !== true) {
        throw new Error(payload?.message ?? "Plivo did not confirm the recording pause");
      }
      setIsRecordingPaused(true);
    } catch (error) {
      toast({
        title: "Could not pause recording",
        description: error instanceof Error ? error.message : "Recording is still active",
        variant: "destructive",
      });
    } finally {
      recordingCommandInProgressRef.current = false;
    }
  }, [toast]);

  const resumeRecording = useCallback(async () => {
    const callLogId = activeCallLogIdRef.current;
    if (!callLogId || recordingCommandInProgressRef.current) return;
    recordingCommandInProgressRef.current = true;
    try {
      const response = await fetch(`/api/calls/${callLogId}/recording/resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const payload = parseRecordingCaptureCommandResult(await response.json().catch(() => null));
      if (!response.ok || payload?.success !== true || payload.paused !== false) {
        throw new Error(payload?.message ?? "Plivo did not confirm recording resume");
      }
      setIsRecordingPaused(false);
    } catch (error) {
      toast({
        title: "Could not resume recording",
        description: error instanceof Error ? error.message : "Recording remains paused",
        variant: "destructive",
      });
    } finally {
      recordingCommandInProgressRef.current = false;
    }
  }, [toast]);

  const sendDtmf = useCallback((digit: string) => {
    if (plivoRef.current) {
      plivoRef.current.client.sendDtmf(digit);
    }
  }, []);

  const transferCall = useCallback(async (target: string, type: "cold" | "warm", displayLabel?: string) => {
    if (!currentCallUuid) {
      setTransferError("No active call to transfer");
      return;
    }
    // Block a new transfer while a previous cancel is still being resolved.
    // The previous cancel must be confirmed (via Pusher conference-enter event)
    // before the agent can start another warm transfer.
    if (warmTransferPhase === "cancelling") {
      setTransferError("Waiting for previous transfer to be cancelled. Please wait a moment.");
      return;
    }
    setIsTransferring(true);
    setTransferError(null);
    try {
      // If currently on hold, unhold first so the transfer setup can work properly.
      // Abort if unhold fails — do not attempt transfer with active hold.
      if (isOnHold && type === "warm") {
        const callLogId = activeCallLogIdRef.current;
        if (callLogId) {
          // Snapshot the pre-hold mute state before unhiding
          preTransferManualMuteRef.current = wasMutedBeforeHoldRef.current;
          try {
            await apiRequest("POST", `/api/calls/${callLogId}/unhold`, {});
            if (plivoRef.current) {
              if (wasMutedBeforeHoldRef.current) {
                plivoRef.current.client.mute();
              } else {
                plivoRef.current.client.unmute();
                setIsMuted(false);
              }
            }
            remoteAudioIntentionallyMutedRef.current = false;
            setRemoteAudioMuted(false);
            setIsOnHold(false);
          } catch (unholdErr: any) {
            console.error("[Plivo] Cannot initiate transfer — unhold failed:", unholdErr);
            setTransferError("Failed to remove hold before transfer. Please try again.");
            return;
          }
        }
      } else {
        // Snapshot current mic state before transfer starts
        preTransferManualMuteRef.current = isMuted;
      }

      const res = await apiRequest("POST", "/api/calls/transfer", {
        callUuid: currentCallUuid,
        target,
        type,
        direction: callDirection,
      });
      const data = await res.json();
      console.log("[Plivo] Transfer initiated:", data);
      if (type === "warm" && data.transferId) {
        activeTransferIdRef.current = data.transferId;
        setWarmTransferPhase("consultation");
        setWarmTransferTarget(displayLabel || target);
      }
      if (type === "cold" && data.agentShouldHangup) {
        console.log("[Plivo] Cold transfer complete — server will disconnect agent leg");
        const coldCallLogId = activeCallLogIdRef.current;
        const coldContactId = activeContactIdRef.current;
        setTimeout(() => {
          callStateRef.current = 'idle';
          setCallState("idle");
          setCurrentCallUuid(null);
          setCallerName("");
          setDialedNumber("");
          setCallDuration(0);
          setIsRecordingPaused(false);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          callStartRef.current = null;
          if (coldCallLogId) {
            setPendingDispositionCallLogId(coldCallLogId);
            setPendingDispositionContactId(coldContactId);
            sessionStorage.setItem("pendingDispositionCallLogId", coldCallLogId);
            if (coldContactId) sessionStorage.setItem("pendingDispositionContactId", coldContactId);
            console.log("[Plivo] Cold transfer — disposition triggered for callLogId:", coldCallLogId);
          }
          flushPendingReconnect();
        }, 2000);
      }
    } catch (err: any) {
      console.error("[Plivo] Transfer failed:", err);
      setTransferError(err.message || "Transfer failed");
    } finally {
      setIsTransferring(false);
    }
  }, [currentCallUuid, callDirection]);

  const mergeTransfer = useCallback(async () => {
    const transferId = activeTransferIdRef.current;
    if (!transferId) {
      setTransferError("No active warm transfer to merge");
      return;
    }
    setIsTransferring(true);
    setTransferError(null);
    try {
      const res = await apiRequest("POST", "/api/calls/transfer/merge", { transferId });
      const data = await res.json();
      console.log("[Plivo] Contact connected to conference:", data);
      setWarmTransferPhase("connected");
    } catch (err: any) {
      console.error("[Plivo] Connect contact failed:", err);
      setTransferError(err.message || "Connect contact failed");
    } finally {
      setIsTransferring(false);
    }
  }, []);

  const completeTransfer = useCallback(async () => {
    // Req 10: capture transferId BEFORE the API call; clear ref only on confirmed success;
    // restore on failure so the agent can retry without losing transfer identity.
    const transferId = activeTransferIdRef.current;
    if (!transferId) {
      setTransferError("No active warm transfer to complete");
      return;
    }
    // Req 2: de-duplicate — if Complete already owns this transfer, ignore second click
    const existingAction = terminalTransferActionRef.current;
    if (existingAction?.transferId === transferId && existingAction?.action === "complete") {
      console.log("[Plivo] completeTransfer: Complete already in progress for", transferId, "— ignoring duplicate");
      return;
    }
    // Req 2: claim terminal ownership BEFORE sending the request
    terminalTransferActionRef.current = { transferId, action: "complete" };
    setIsTransferring(true);
    setTransferError(null);
    // DO NOT clear activeTransferIdRef here — clear only after success (Req 10).
    try {
      // Req 6: use raw fetch so res.status is readable before throwing.
      // apiRequest throws for all non-2xx, making the 503 check unreachable.
      const res = await fetch("/api/calls/transfer/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ transferId }),
      });
      // Handle 503 retriable failures: preserve state so agent can retry
      if (res.status === 503) {
        const errData = await res.json().catch(() => ({}));
        if (errData.retryable) {
          console.warn("[Plivo] Complete transfer retriable failure — server returning 503, will retry. Reason:", errData.reason);
          setTransferError("Completing transfer, please wait a moment…");
          // Restore ref so the agent can retry (Req 10)
          activeTransferIdRef.current = transferId;
          // Keep terminalTransferActionRef set so onCallTerminated still sees "complete" ownership
          return;
        }
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Complete transfer failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      console.log("[Plivo] Transfer completed:", data);
      // Req 10: Only clear the ref now that success is confirmed.
      activeTransferIdRef.current = null;
      terminalTransferActionRef.current = null;
      setWarmTransferPhase("merged");

      if (data.agentShouldHangup) {
        // The server already restored agent status (Req 7). The timer below is a
        // safety fallback only, in case onCallTerminated never fires.
        console.log("[Plivo] Warm transfer completed — server restored agent status; browser fallback timer started");
        const warmCallLogId = activeCallLogIdRef.current;
        const warmContactId = activeContactIdRef.current;
        setTimeout(() => {
          if (callStateRef.current === 'idle') {
            // onCallTerminated already ran, but if it ran without an activeTransferId
            // (because completeTransfer cleared the ref before the event fired),
            // warmTransferPhase was never reset from "merged". Fix it here.
            setWarmTransferPhase("idle");
            setWarmTransferTarget(null);
            return;
          }
          callStateRef.current = 'idle';
          setCallState("idle");
          setCurrentCallUuid(null);
          setCallerName("");
          setDialedNumber("");
          setCallDuration(0);
          setIsRecordingPaused(false);
          setWarmTransferPhase("idle");
          setWarmTransferTarget(null);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          callStartRef.current = null;
          if (warmCallLogId) {
            setPendingDispositionCallLogId(warmCallLogId);
            setPendingDispositionContactId(warmContactId);
            sessionStorage.setItem("pendingDispositionCallLogId", warmCallLogId);
            if (warmContactId) sessionStorage.setItem("pendingDispositionContactId", warmContactId);
            console.log("[Plivo] Warm transfer — disposition triggered for callLogId:", warmCallLogId);
          }
          flushPendingReconnect();
        }, 2000);
      }
    } catch (err: any) {
      console.error("[Plivo] Complete transfer failed:", err);
      // Req 10: restore ref so the agent can retry
      activeTransferIdRef.current = transferId;
      // Req 2: keep terminalTransferActionRef so onCallTerminated still suppresses cancel
      // while the agent still has the retry opportunity
      setTransferError(err.message || "Complete transfer failed");
    } finally {
      setIsTransferring(false);
    }
  }, []);

  // Req 4: Idempotent cancel finalizer — called by BOTH Pusher and polling.
  // Only finalizes if the transfer IDs still match (prevents clearing a newer transfer).
  // `reconnectFailed` = true means the customer couldn't be reconnected at all.
  const finalizeCancelTransfer = useCallback((
    transferId: string,
    reconnectFailed = false,
  ) => {
    const currentId = activeTransferIdRef.current;
    if (!currentId || currentId !== transferId) {
      console.log(`[Plivo] finalizeCancelTransfer: skipped — id mismatch (current=${currentId}, event=${transferId})`);
      return;
    }
    if (cancelPollIntervalRef.current) {
      clearInterval(cancelPollIntervalRef.current);
      cancelPollIntervalRef.current = null;
    }
    activeTransferIdRef.current = null;
    terminalTransferActionRef.current = null; // Req 2: release ownership
    setIsTransferring(false);
    setWarmTransferPhase("idle");
    setWarmTransferTarget(null);
    if (reconnectFailed) {
      setTransferError("Transfer cancelled but customer could not be reconnected to the agent");
    } else {
      setTransferError(null);
    }
    console.log(`[Plivo] finalizeCancelTransfer: completed for ${transferId} (reconnectFailed=${reconnectFailed})`);
  }, []);

  const cancelTransfer = useCallback(async () => {
    // Req 10: capture transferId before API call so identity is preserved even if
    // something clears the ref while the request is in-flight.
    const transferId = activeTransferIdRef.current;
    if (!transferId) return;
    // Block double-cancel while already cancelling
    if (warmTransferPhase === "cancelling") return;
    // Req 2: de-duplicate — if any terminal action already owns this transfer, ignore
    const existingCancelAction = terminalTransferActionRef.current;
    if (existingCancelAction?.transferId === transferId) {
      console.log("[Plivo] cancelTransfer: terminal action already in progress for", transferId, "(action:", existingCancelAction.action, ") — ignoring duplicate");
      return;
    }
    // Req 2: claim terminal ownership BEFORE sending the request
    terminalTransferActionRef.current = { transferId, action: "cancel" };
    setIsTransferring(true);
    setTransferError(null);
    try {
      const res = await apiRequest("POST", "/api/calls/transfer/cancel", { transferId });
      const data = await res.json();
      console.log("[Plivo] Transfer cancel response:", data);

      // Server has accepted the cancel and is reconnecting the customer.
      // Retain activeTransferIdRef so Pusher events can match the transfer.
      // The browser stays in "cancelling" until:
      //   • Pusher transfer-cancelled (customerReconnected:true) — conference-enter confirmed
      //   • polling confirms customerReconnected=true (Req 4 bounded fallback)
      //   • Pusher transfer-cancel-reconnect-failed — server gave up
      //   • poll timeout (~37s) — finalize with reconnect-failed error
      // Do NOT clear activeTransferIdRef here — finalizeCancelTransfer owns that transition.
      setIsTransferring(false);
      setWarmTransferPhase("cancelling");

      // Clear any stale poll interval from a previous cancel attempt
      if (cancelPollIntervalRef.current) {
        clearInterval(cancelPollIntervalRef.current);
        cancelPollIntervalRef.current = null;
      }

      // Req 4: Start bounded status polling alongside Pusher.
      // Poll at ~1.5 s intervals; give up at ~37 s (covers 30 s fallback Dial + buffer).
      const CANCEL_POLL_INTERVAL_MS = 1500;
      const CANCEL_POLL_MAX_MS = 37_000;
      let pollElapsedMs = 0;
      cancelPollIntervalRef.current = setInterval(async () => {
        pollElapsedMs += CANCEL_POLL_INTERVAL_MS;
        const currentTid = activeTransferIdRef.current;
        if (!currentTid || currentTid !== transferId) {
          // Already finalized by Pusher — stop polling
          if (cancelPollIntervalRef.current) {
            clearInterval(cancelPollIntervalRef.current);
            cancelPollIntervalRef.current = null;
          }
          return;
        }
        try {
          const statusRes = await fetch(`/api/calls/transfer/${encodeURIComponent(transferId)}/status`, {
            credentials: "include",
          });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.customerReconnected === true) {
              console.log(`[Plivo] Cancel poll: customerReconnected=true for ${transferId} — finalizing`);
              finalizeCancelTransfer(transferId, false);
              return;
            }
            if (statusData.customerReconnectFailed === true) {
              console.log(`[Plivo] Cancel poll: customerReconnectFailed=true for ${transferId} — finalizing with error`);
              finalizeCancelTransfer(transferId, true);
              return;
            }
          }
        } catch (pollErr: any) {
          console.log(`[Plivo] Cancel poll request failed (non-fatal): ${pollErr.message}`);
        }
        if (pollElapsedMs >= CANCEL_POLL_MAX_MS) {
          console.warn(`[Plivo] Cancel poll timeout for ${transferId} after ${pollElapsedMs}ms — finalizing with reconnect-failed`);
          finalizeCancelTransfer(transferId, true);
        }
      }, CANCEL_POLL_INTERVAL_MS);

      return;
    } catch (err: any) {
      console.error("[Plivo] Cancel failed:", err);
      setTransferError(err.message || "Cancel failed");
      setIsTransferring(false);
    }
  }, [warmTransferPhase, finalizeCancelTransfer]);

  const transferToSurvey = useCallback(async (surveyTemplateId?: string) => {
    const callLogId = activeCallLogIdRef.current;
    const agentCallUuid = plivoRef.current?.client?.callUUID || currentCallUuid;
    if (!agentCallUuid) {
      console.error("[Plivo] Cannot transfer to survey: missing callUUID");
      return;
    }
    try {
      const body: Record<string, any> = { callLogId: callLogId || undefined, agentCallUuid };
      if (surveyTemplateId) body.surveyTemplateId = surveyTemplateId;
      await apiRequest("POST", "/api/csat/in-call-transfer", body);
      console.log("[Plivo] Caller transferred to CSAT survey");
    } catch (err: any) {
      console.error("[Plivo] Survey transfer failed:", err);
      throw err;
    }
  }, [currentCallUuid]);

  return (
    <PlivoContext.Provider
      value={{
        isRegistered,
        callState,
        callDirection,
        currentCallUuid,
        callerName,
        dialedNumber,
        activeCustomerNumber,
        callerKind,
        ourNumber,
        incomingInternalCall,
        isCallbackActiveCall,
        callDuration,
        isMuted,
        isOnHold,
        isRecordingPaused,
        connectedEndpointId,
        activeContactId,
        activeCallLogId: activeCallLogIdRef.current,
        activePdCampaignId,
        pendingDispositionCallLogId,
        pendingDispositionContactId,
        campaignDispositionConfig,
        setCampaignDispositionConfig,
        isDispositionBlocked,
        isTransferring,
        transferError,
        warmTransferPhase,
        warmTransferTarget,
        clearPendingDisposition,
        login,
        autoConnect,
        disconnect,
        makeCall,
        dncWarning,
        clearDncWarning,
        answerCall,
        hangupCall,
        rejectCall,
        toggleMute,
        toggleHold,
        pauseRecording,
        resumeRecording,
        sendDtmf,
        transferCall,
        mergeTransfer,
        completeTransfer,
        cancelTransfer,
        registrationError,
        micBlockedDialogOpen,
        setMicBlockedDialogOpen,
        micBlockedMode,
        plivoRef,
        transferToSurvey,
        incomingQueueName,
        incomingIvrMenuName,
        incomingCampaignName,
        monitor: {
          active: monitorActive,
          sessionId: monitorSessionId,
          callLogId: monitorCallLogId,
          mode: monitorMode,
          targetName: monitorTargetName,
          agentName: monitorAgentName,
          contactNumber: monitorContactNumber,
          callStartedAt: monitorCallStartedAt,
          callUuid: monitorCallUuid,
          connectedAt: monitorConnectedAt,
          setMode: setMonitorMode,
        },
        startMonitor,
        hangupMonitor,
        expectMonitorCall,
        cancelMonitorExpect,
        lastCallEndReason,
        clearCallEndReason: () => setLastCallEndReason(null),
        requestSipReconnect,
      }}
    >
      {children}
    </PlivoContext.Provider>
  );
}

export function usePlivo() {
  const context = useContext(PlivoContext);
  if (!context) {
    throw new Error("usePlivo must be used within a PlivoProvider");
  }
  return context;
}
